import { useEffect, useMemo, useRef, useState } from "react";
import { FilterMissing } from "./AllergenFilter.tsx";
import type { KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import {
  ENTRANCE_TITLE,
  windowText,
  entranceDraft,
  applyProofEdit,
  menuSpots,
  withMenuText,
  LANG_LABELS,
  menuDishes,
  menuView,
  phraseMissing,
  translationState,
  viewPhrases,
  entranceConflicts,
  withCatalogPrices,
  cardLangKey,
  cardLangOf,
  cardLangsOf,
  NO_SCALE,
  isNoScale,
  scaleDesign,
  scaleOf,
  scaleSummary,
  LAYOUTS,
  LAYOUT_HINTS,
  LAYOUT_LABELS,
  SORT_LABELS,
  addDishes,
  allCategories,
  catalogItem,
  copyOf,
  countsOf,
  drinksLike,
  hasDrinks,
  isDrink,
  kindOf,
  lacksAmount,
  onPart,
  partMenu,
  partOf,
  parseVariants,
  variantsText,
  youthHint,
  HOUSE_STYLE,
  tagKey,
  MAX_DESIGNS,
  defaultDesigns,
  designsOf,
  presetDesign,
  editsCatalog,
  fontOf,
  pageOf,
  editsMenus,
  emptySection,
  findDishes,
  flatten,
  isDietTag,
  itemCount,
  itemVariants,
  moveItem,
  newMenuLike,
  parseNumberGroups,
  courseCount,
  tidyCourses,
  parsePrice,
  plainPrice,
  priceDiffs,
  publishes,
  placesOf,
  uniqueItems,
  removeDish,
  scopeOf,
  dishesIn,
  sameLabel,
  sectionsFromCategories,
  isSubsection,
  newSubsection,
  secondLevelsIn,
  subsectionsFromCategories,
  sortItems,
  topCategory,
  textLabel,
  uuidv7,
  guessedOn,
  guessedQuestion,
} from "@speisekarte/core";
import type { CardLang, CardText, Design, Designs, Dish, DishKind, DrinksPart, Menu, MenuInput, MenuScale, MenuSection, Part, PresetId, Scope, SortKey, Structure, StyleKey } from "@speisekarte/core";
import { downloadPdf, downloadWord, entranceNow, isInsert, planPdfSlot, printMenu, printMenuOf, publishPdfSlot } from "../output.ts";
import { OwnPagesChoice } from "./OwnPages.tsx";
import { ApiError, api, dishesById, messageOf, store, textsById, useStore } from "../store.ts";
import { TagSymbol } from "../icon.tsx";
import { useKept } from "../kept.ts";
import type { Data } from "../store.ts";
import type { PrintMenu } from "../menuHtml.ts";
import { GRIP_PATH, useSortable } from "../sortable.ts";
import type { Slot } from "../sortable.ts";
import { useWide } from "../wide.ts";
import { Toast, useToast } from "./Toast.tsx";
import { DesignPanel, FitList, PartRow, PdfPreview } from "./Design.tsx";
import type { OpenPart } from "./Design.tsx";
import { SymbolInput, SymbolTextarea } from "./Symbols.tsx";
import { ProofBox, openEdits } from "./Proof.tsx";
import type { OpenEdit } from "./Proof.tsx";
import { rememberHere } from "../tabs.ts";
import { NetLangs, outdated, recommendationOld } from "./Web.tsx";
import { pdfMarkOf } from "../outside.ts";
import { OutsideFix } from "./Outside.tsx";
import { PlannedList, TimesSheet, WhenChoice, firstWhen, whenAt } from "./Times.tsx";
import type { When } from "./Times.tsx";
import { timedOn, whenText } from "../timing.ts";
import { drinksInPlay } from "./Dishes.tsx";
import { CardTexts, reviewHref, textsAction, textsCount, useTextStatus } from "./TextWork.tsx";
import { useModel } from "../model.ts";
import { Choice } from "./LangChoice.tsx";

/**
 * Two sets of styles alike as the server keeps them: compared after the same cleaning, so the order
 * of their fields does not count („Unterschiedliche Formatierung für Speisen und Getränke“ adds the
 * drinks' styles at the end, the server keeps them after the food's; Martin, 27.10.2026: saved, yet
 * „Speichern“ stayed on and leaving warned of changes not saved).
 */
const sameDesigns = (a: Designs, b: Designs) => JSON.stringify(designsOf(a)) === JSON.stringify(designsOf(b));

/** Drafts of menus not yet saved, kept while the app runs (leaving the editor does not lose them). */
const drafts = new Map<string, { draft: MenuInput; base: { id: string; version: number } | null }>();
/** Which pane the editor of a menu showed and which section took the dishes, to show them again on coming back. */
const panes = new Map<string, { tab: Tab; side: Side; target: string | null; step: Step }>();
/** The panes of step 1 on a phone, and on the iPad at the left (S22: „Getränke“ beside „Speisen“). */
type Tab = "pick" | "drinks" | "texts" | "menu";
type Side = "pick" | "drinks" | "texts";

/**
 * The styles as changed in step 2 and not yet saved; they are the business's, so one for all menus.
 * With the version they were changed from (S55), so a save over a newer one is asked about.
 */
let designDraft: { designs: Designs; version: number | null } | null = null;

/** The three steps of making a menu: what is on it, how it looks, what becomes of it. */
type Step = 1 | 2 | 3;
const STEPS: readonly { label: string; hint: string }[] = [
  { label: "Inhalt", hint: "Speisen, Textbausteine, Abschnitte, Preise" },
  { label: "Aussehen", hint: "Darstellung, Stil" },
  { label: "Drucken & Netz", hint: "Drucken, PDF, Word, im Netz" },
];
const inputOf = (m: Menu): MenuInput => ({ title: m.title, subtitle: m.subtitle, note: m.note, structure: m.structure, layout: m.layout, showNumbers: m.showNumbers, showTags: m.showTags, sections: m.sections, style: m.style, ...(m.drinks ? { drinks: m.drinks } : {}), ...(m.scale ? { scale: m.scale } : {}), ...(m.ownPages ? { ownPages: m.ownPages } : {}) });

/**
 * A menu to edit, or the word that it is gone. Apart from the editor, so its hooks stay the same: a
 * menu made on another device is „gone“ here until the data comes, then the editor begins with it.
 */
export function Editor({ id, params }: { id: string; params: URLSearchParams }) {
  const data = useStore().data!;
  const fresh = id === "neu";
  const key = fresh ? `neu:${params.toString()}` : id;
  if (!fresh && !data.menus.some((m) => m.id === id) && !drafts.has(key)) {
    return (
      <div className="card">
        <p>Diese Speisekarte gibt es nicht mehr. Es bleiben die letzten {data.settings.keepMenus} Speisekarten.</p>
        <a className="button" href="#/speisekarten">
          Zu den Speisekarten
        </a>
      </div>
    );
  }
  return <MenuEditor id={id} params={params} />;
}

/**
 * „#/speisekarte/<id>“ or „#/speisekarte/neu?form=list|sections&von=<id>“: one menu to make, change and send out,
 * in three steps (Martin, 23.10.2026): 1 Inhalt – dishes and texts, list or sections, prices, menus
 * with one price; 2 Aussehen – layout and style, the PDF beside it; 3 Drucken & Netz – print, PDF,
 * Word, on the net. Saving stays at the top, in every step.
 */
function MenuEditor({ id: route, params }: { id: string; params: URLSearchParams }) {
  const s = useStore();
  const data = s.data!;
  const fresh = route === "neu";
  /** The key it was opened with: a new menu's until it is saved. */
  const opened = fresh ? `neu:${params.toString()}` : route;
  const stored = fresh ? null : (data.menus.find((m) => m.id === route) ?? null);
  const [state, setState] = useState(() => {
    const kept = drafts.get(opened);
    if (kept) return { ...kept, dirty: true };
    if (stored) return { draft: inputOf(stored), base: { id: stored.id, version: stored.version }, dirty: false };
    const from = params.get("von") ? data.menus.find((m) => m.id === params.get("von")) : null;
    // A new list at the entrance (S42) begins as a copy of the whole menu with its own title, to
    // shorten, one sheet (`entranceDraft`). A new recommendation from the one before (S37, L4) keeps
    // its title: the guests see it.
    if (from && params.get("aushang")) return { draft: entranceDraft(inputOf(from), data.menus, Date.now()), base: null, dirty: true };
    const copy = from ? copyOf(inputOf(from), Date.now()) : null;
    const title = params.get("empfehlung") && from ? from.title : null;
    return { draft: copy ? (title ? { ...copy, title } : copy) : newMenuLike(Date.now(), (params.get("form") as Structure) || "list", data.menus), base: null, dirty: true };
  });
  const { draft, base, dirty } = state;
  // Saved for the first time, a new menu stays in this editor (its question about prices, its pane)
  // and is that menu from then on: printing notes the list at the entrance, an insert prints as one,
  // a draft is kept under its id (the address alone changed without the route, 02.11.2026).
  const id = base?.id ?? route;
  const key = base?.id ?? opened;
  // A new menu always begins at step 1 (Martin, 25.10.2026); only a draft not yet saved comes back where it was.
  const [pane] = useState(() => (fresh && !drafts.has(opened) ? undefined : panes.get(opened)));
  const [tab, setTab] = useState<Tab>(() => pane?.tab ?? (fresh ? "pick" : "menu"));
  /** On the iPad: what the left pane shows. */
  const [side, setSide] = useState<Side>(() => pane?.side ?? "pick");
  const [target, setTarget] = useState<string | null>(() => pane?.target ?? null);
  // „Übersetzen“ under „Im Netz“ opens step 2 (Martin, 01.11.2026).
  const [step, setStep] = useState<Step>(() => (params.get("schritt") === "2" ? 2 : (pane?.step ?? 1)));
  useEffect(() => void panes.set(key, { tab, side, target, step }), [key, tab, side, target, step]);
  const [busy, setBusy] = useState(false);
  const [diffs, setDiffs] = useState<ReturnType<typeof priceDiffs> | null>(null);
  // Saved (S62, Z-0): until closed or changed again, what outside still shows of the old state.
  const [afterSave, setAfterSave] = useState(false);
  const [preview, setPreview] = useState(false);
  const toast = useToast();
  /** The newest „Speichern“, for the one that first lets a field hand over its text. */
  const saveNow = useRef<() => Promise<string | null>>(async () => null);
  const wide = useWide();
  const dishes = dishesById(data);
  const role = s.role;
  const canEdit = editsMenus(role);

  // The styles (S15), changed in step 2 and saved with the menu (S17).
  const savedDesigns = useMemo(() => designsOf(data.designs ?? defaultDesigns()), [data.designs]);
  const savedVersion = data.designsVersion ?? null;
  const [held, setHeld] = useState(() => designDraft ?? { designs: savedDesigns, version: savedVersion });
  const designs = held.designs;
  const setDesigns = (fn: (d: Designs) => Designs) => setHeld((h) => ({ ...h, designs: fn(h.designs) }));
  const designsDirty = !sameDesigns(designs, savedDesigns);
  const lastSaved = useRef(savedDesigns);
  useEffect(() => {
    // Saved here or on another device: taken over, unless changed here meanwhile. The state before
    // is taken now: React may run the update only later, after `lastSaved` already holds the new one
    // (opened over data kept on the device, the editor then kept the old styles as changed here and
    // saved them over the new ones, S55).
    const before = lastSaved.current;
    lastSaved.current = savedDesigns;
    setHeld((h) => (sameDesigns(h.designs, before) || sameDesigns(h.designs, savedDesigns) ? { designs: savedDesigns, version: savedVersion } : h));
  }, [savedDesigns, savedVersion]);
  useEffect(() => void (designDraft = designsDirty ? held : null), [held, designsDirty]);
  const [openStyle, setOpenStyle] = useKept<OpenPart | null>(`${opened}:style`, null);

  // Another device saved this menu while nothing was changed here: take its state.
  useEffect(() => {
    if (stored && !dirty && base && stored.version !== base.version) setState({ draft: inputOf(stored), base: { id: stored.id, version: stored.version }, dirty: false });
  }, [stored?.version]);
  useEffect(() => {
    if (dirty) drafts.set(key, { draft, base });
    else drafts.delete(key);
  }, [draft, dirty]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty || designsDirty) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, designsDirty]);

  const edit = (fn: (m: MenuInput) => MenuInput) => setState((st) => ({ ...st, draft: fn(st.draft), dirty: true }));
  const onMenu = new Set(draft.sections.flatMap((x) => x.items.map((i) => i.dish)));
  // The section tapped last; else the last of the food (S22: drinks then go by their category).
  const chosenSection = draft.sections.find((x) => x.id === target)?.id;
  const targetSection = chosenSection ?? [...draft.sections].reverse().find((x) => partOf(x) === "food")?.id ?? draft.sections[draft.sections.length - 1]?.id;
  // Where a tap in the picker puts a dish, and so whether it counts as „on“ (C1): the set menu chosen,
  // else the card. The food goes to the section chosen or the last of the food; the drinks to the one
  // chosen or by their category.
  const foodTarget = draft.sections.find((x) => x.id === chosenSection && partOf(x) === "food") ?? [...draft.sections].reverse().find((x) => partOf(x) === "food");
  // A set menu chosen takes drinks too (S52): a wine to its last course.
  const drinkTarget = draft.sections.find((x) => x.id === chosenSection && (partOf(x) === "drinks" || x.kind === "set"));
  const scopes: Record<DishKind, Scope> = { food: scopeOf(draft, foodTarget?.id), drink: scopeOf(draft, drinkTarget?.id) };
  // `ors`: dishes that go into a set menu as an alternative to the one before („21/22“, S32).
  const add = (list: Dish[], ors: ReadonlySet<string> = new Set()) => {
    const on = { food: dishesIn(draft, scopes.food), drink: dishesIn(draft, scopes.drink) };
    const fresh = list.filter((d) => !on[kindOf(d)].has(d.id) && (on[kindOf(d)].add(d.id), true));
    if (fresh.length) edit((m) => addDishes(m, fresh, chosenSection ?? targetSection, drinksLike(data.menus), data.drinkCategories, ors));
    return fresh.length;
  };
  // Drinks are in play (S22) when the business has some or this menu shows some; without, all is as before.
  const drinksOn = drinksInPlay(data) || hasDrinks(draft);
  /** A text of the catalogue (S14) at the end of the chosen section, or off the menu. */
  const toggleText = (t: CardText) =>
    edit((m) =>
      onMenu.has(t.id)
        ? removeDish(m, t.id)
        : { ...m, sections: m.sections.map((x) => (x.id === targetSection ? { ...x, items: [...x.items, { dish: t.id, price: null, note: "" }] } : x)) },
    );
  const mine = designs.list.some((x) => x.id === draft.style) ? draft.style! : designs.main;
  const menuDesign = (designs.list.find((x) => x.id === mine) ?? designs.list[0]!).design;
  // The language of print, PDF and Word (S36); only with a further one switched on.
  const langs = data.settings.languages ?? [];
  const [langKey, setLangKey] = useKept("print:lang", "de");
  // Kept as one object while the choice stays, so the preview does not set the PDF again.
  const printLang = useMemo(() => cardLangOf(langKey, langs), [langKey, langs.join()]);
  const pm = () => printMenuOf(draft, data, new Date(), menuDesign, id, printLang);
  // The language of the card (S36), in step 2 with translating (Martin, 01.11.2026) and in step 3 for print: one choice for both.
  const langChips = langs.length > 0 && (
    <Choice
      label="Sprache der Speisekarte"
      options={cardLangsOf(langs).map((c) => ({ key: cardLangKey(c), label: c ? (c.both ? `Deutsch und ${LANG_LABELS[c.lang]}` : LANG_LABELS[c.lang]) : "Deutsch" }))}
      value={cardLangKey(printLang)}
      onChange={setLangKey}
      testid="print-lang"
    />
  );
  const anyDirty = dirty || designsDirty;
  // „Texte prüfen und übersetzen“ for this card (S61): the German and each language, counted from the draft as it is.
  const savedId = base?.id ?? null;
  const savedMenu = savedId ? data.menus.find((m) => m.id === savedId) : undefined;
  const cardMenu = useMemo(() => ({ ...draft, id: savedId ?? "neu" }), [draft, savedId]);
  const cardStatus = useTextStatus(data, cardMenu);
  // In texts only (S61 § 11): „4 Texte dieser Karte sind noch nicht auf Englisch übersetzt.“
  const langCounts = printLang ? cardStatus.counts[printLang.lang] : undefined;
  const toLook = langCounts ? langCounts.review.dishes + langCounts.review.texts + langCounts.stale.dishes + langCounts.stale.texts : 0;
  const missing = langCounts ? langCounts.open.dishes + langCounts.open.texts : 0;
  const missingText = printLang && missing > 0 ? `${textsCount(missing)} dieser Karte ${missing === 1 ? "ist" : "sind"} noch nicht auf ${LANG_LABELS[printLang.lang]} übersetzt.` : "";
  const model = useModel();
  // Step 2 (Martin, 01.11.2026; S61): the language of the card, and where its German and each language stand, with the way to finish them.
  const langBlock = cardStatus.targets.length > 0 && (
    <div className="card-lang" data-testid="card-lang">
      {langChips}
      <CardTexts data={data} status={cardStatus} menu={cardMenu} save={canEdit && (anyDirty || !savedId) ? saveAll : null} />
    </div>
  );
  const changeDesign = (fn: (d: Design) => Design) => setDesigns((d) => ({ ...d, list: d.list.map((x) => (x.id === mine ? { ...x, design: { ...fn(x.design), preset: "" } } : x)) }));
  const usePreset = (p: PresetId) => setDesigns((d) => ({ ...d, list: d.list.map((x) => (x.id === mine ? { ...x, design: presetDesign(p) } : x)) }));
  /** A tap in the PDF: that part's style, open and in view. */
  const pickStyle = (k: OpenPart) => {
    setOpenStyle(k);
    const id = k === "translation" ? "part-translation" : k.startsWith("drink:") ? `style-drink-${k.slice(6)}` : k.startsWith("setdrink:") ? `style-setdrink-${k.slice(9)}` : `style-${k}`;
    // A drink in a set menu still coupled (S53): its group with the switch to set it apart.
    requestAnimationFrame(() => (document.querySelector(`[data-testid="${id}"]`) ?? (k.startsWith("setdrink:") ? document.querySelector(`[data-testid="set-drinks-group"]`) : null))?.scrollIntoView({ block: "center", behavior: "smooth" }));
  };

  saveNow.current = saveAll;
  /** „Speichern“: the styles when changed, then the menu. */
  async function saveAll(): Promise<string | null> {
    // A field that takes its text on leaving (a price, a menu's further prices) may still have the
    // focus: Safari keeps it there on a tap on „Speichern“. Saved without its text, the menu was
    // „changed“ again as soon as the field was left (Martin, 27.10.2026). Its text first, then the
    // newest state.
    const field = document.activeElement;
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      // Drawn at once, so the newest „Speichern“ has the field's text.
      flushSync(() => field.blur());
      return saveNow.current();
    }
    let tookTheirs = false;
    if (designsDirty && editsCatalog(role)) {
      setBusy(true);
      try {
        try {
          await store.change(() => api("/api/designs", { method: "PUT", body: { designs, version: held.version } }));
        } catch (err) {
          // Changed on another device meanwhile (S55): asked, as for a menu; else theirs is taken.
          if (!(err instanceof ApiError && err.code === "conflict")) throw err;
          if (confirm("Der Stil wurde inzwischen auf einem anderen Gerät geändert. Deinen Stil trotzdem speichern? Der andere geht dabei verloren.")) {
            await store.change(() => api("/api/designs", { method: "PUT", body: { designs, version: null } }));
          } else {
            const theirs = err.current as { designs: Designs | null; designsVersion: number } | undefined;
            await store.refresh();
            if (theirs) setHeld({ designs: designsOf(theirs.designs ?? defaultDesigns()), version: theirs.designsVersion });
            tookTheirs = true;
          }
        }
      } catch (err) {
        toast.show(messageOf(err));
        return null;
      } finally {
        setBusy(false);
      }
    }
    if (dirty || !base) {
      const saved = await save();
      if (saved && tookTheirs) toast.show("Gespeichert, mit dem Stil vom anderen Gerät");
      return saved;
    }
    toast.show(tookTheirs ? "Stil vom anderen Gerät übernommen" : "Gespeichert");
    return base.id;
  }

  async function save(): Promise<string | null> {
    if (!canEdit) return base?.id ?? null;
    setBusy(true);
    try {
      let saved: Menu;
      if (!base) {
        saved = (await api<{ menu: Menu }>("/api/menus", { body: { menu: draft } })).menu;
        drafts.delete(opened);
        // The address of the menu, with the same pane and section when it is opened again.
        panes.set(saved.id, { tab, side, target, step });
        history.replaceState(null, "", `#/speisekarte/${saved.id}`);
      } else {
        try {
          saved = (await api<{ menu: Menu }>(`/api/menus/${base.id}`, { method: "PUT", body: { menu: draft, version: base.version } })).menu;
        } catch (err) {
          if (!(err instanceof ApiError && err.code === "conflict")) throw err;
          if (!confirm("Diese Speisekarte wurde inzwischen auf einem anderen Gerät geändert. Deine Fassung trotzdem speichern? Die andere geht dabei verloren.")) return null;
          saved = (await api<{ menu: Menu }>(`/api/menus/${base.id}`, { method: "PUT", body: { menu: draft, version: null } })).menu;
        }
      }
      setState({ draft: inputOf(saved), base: { id: saved.id, version: saved.version }, dirty: false });
      await store.refresh();
      setAfterSave(true);
      const d = priceDiffs(saved, dishes);
      if (d.length && editsCatalog(role)) setDiffs(d);
      else toast.show("Gespeichert");
      return saved.id;
    } catch (err) {
      toast.show(messageOf(err));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (!despiteEntrance("Trotzdem veröffentlichen")) return;
    const guessed = guessedOn(draft, dishesById(data));
    if (guessed > 0 && !confirm(guessedQuestion(guessed))) return;
    const others = data.published && data.published.menu !== base?.id ? data.menus.find((m) => m.id === data.published!.menu) : null;
    if (others && !confirm(`Gäste sehen zurzeit „${others.title}“. Durch „${draft.title}“ ersetzen? Der QR-Code bleibt derselbe.`)) return;
    const menuId = anyDirty || !base ? await saveAll() : base.id;
    if (!menuId) return;
    try {
      await store.change(() => api("/api/publish", { body: { menu: menuId } }));
      toast.show("Veröffentlicht. QR-Code und Aufkleber unter „Im Netz“.");
    } catch (err) {
      toast.show(messageOf(err));
    }
  }

  /** „Als Empfehlungskarte zeigen“ (S37, L4; the name S59): saved first, then above the QR-Code-Speisekarte at once; the service may too. */
  async function asRecommendation() {
    const menuId = anyDirty || !base ? await saveAll() : base.id;
    if (!menuId) return;
    try {
      await store.change(() => api("/api/publish/recommendations", { body: { menu: menuId } }));
      toast.show("Ist jetzt die Empfehlungskarte. Gedruckt als Einlegeblatt.");
    } catch (err) {
      toast.show(messageOf(err));
    }
  }

  const outputs = async (fn: () => Promise<void>) => {
    if (!despiteEntrance("Trotzdem drucken")) return;
    try {
      await fn();
    } catch (err) {
      toast.show(messageOf(err));
    }
  };

  const published = base && data.published?.menu === base.id;
  const webOld = Boolean(published) && outdated(data);
  // The recommendations (S37): from a second menu on (S38), for any but the one on the net.
  const recommends = canEdit && Boolean(data.published) && !published && (data.menus.length >= 2 || !base);
  const isRecommendation = Boolean(base) && data.settings.recommendationsMenu === base!.id && Boolean(data.published);
  const recOld = isRecommendation && recommendationOld(data);
  // Später ins Netz (S62, Bild 5): with the time control, when the menu goes on the net.
  const timed = timedOn(data);
  const [qrWhen, setQrWhen] = useState<When>({ kind: "now" });
  const [recWhen, setRecWhen] = useState<When>(() => firstWhen("recommendations"));
  // Planned already (Tutorial, 04.11.2026): the box says when, and publishing now is no longer the first thing to tap.
  const plannedFor = (what: "menu" | "recommendations") => (timed && base ? (data.settings.planned ?? []).filter((p) => p.what === what && p.menu === base.id && !p.folder).sort((a, b) => a.at - b.at)[0] : undefined);
  const qrPlanned = plannedFor("menu");
  const recPlanned = plannedFor("recommendations");
  /** Planned for a moment (S62): saved first, as publishing does. */
  async function plan(what: "menu" | "recommendations", when: When) {
    const at = whenAt(when, what);
    if (at === null || !Number.isFinite(at)) return toast.show("Bitte Tag und Uhrzeit angeben.");
    const menuId = anyDirty || !base ? await saveAll() : base.id;
    if (!menuId) return;
    try {
      await store.change(() => api("/api/plan", { body: { what, menu: menuId, at } }));
      toast.show(`Geplant: ${whenText(at, Date.now())} ${what === "menu" ? "als QR-Code-Speisekarte" : "als Empfehlungskarte"}. Änderungen bis dahin kommen mit.`);
      if (what === "menu") setQrWhen({ kind: "now" });
      else setRecWhen({ kind: "now" });
    } catch (err) {
      toast.show(messageOf(err));
    }
  }
  // The list at the entrance (S42): its prices are the catalogue's; another menu is checked against it.
  const isEntrance = Boolean(base) && data.settings.entranceMenu === base!.id;
  const entranceMenu = data.menus.find((m) => m.id === data.settings.entranceMenu) ?? null;
  const conflicts = entranceMenu && !isEntrance ? entranceConflicts(draft, entranceMenu, dishes) : null;
  const conflictLines = conflicts
    ? [
        ...conflicts.dishes.flatMap((c) => [...(c.to !== null ? [`„${c.name}“ für ${euro(c.from)}, hier ${euro(c.to)}`] : []), ...c.variants.map((v) => `„${c.name}“, ${v.label}, für ${euro(v.from)}, hier ${euro(v.to)}`)]),
        ...conflicts.sets.map((s) => `das Menü „${s.title}“ für ${euro(s.entrance)}, hier ${euro(s.here)}`),
      ]
    : [];
  /** „So geht's nicht“ (S42): a price here that the list at the entrance shows otherwise; asked before printing and publishing. */
  const despiteEntrance = (go: string) => conflictLines.length === 0 || confirm(`So geht's nicht: Am Eingang hängt ${conflictLines.join("; ")}. Bitte die Preise angleichen (Schritt 3). ${go}?`);
  const printedOld = isEntrance && (data.settings.entrancePrinted?.menu !== base!.id || data.settings.entrancePrinted?.digest !== entranceNow(data));
  const [entranceAsk, setEntranceAsk] = useState<string[] | null>(null);
  /** „Als Aushang am Eingang“ (S42): saved first; with prices of its own that differ, the app asks which way. */
  async function asEntrance(prices?: "catalog" | "menu") {
    const menuId = anyDirty || !base ? await saveAll() : base.id;
    if (!menuId) return;
    try {
      await store.change(() => api("/api/entrance", { body: { menu: menuId, ...(prices ? { prices } : {}) } }));
      setEntranceAsk(null);
      toast.show("Hängt am Eingang aus. Die Preise kommen jetzt aus dem Katalog; bitte drucken und aushängen.");
    } catch (err) {
      if (err instanceof ApiError && err.code === "price_diffs" && Array.isArray(err.current)) {
        const diffs = err.current as ReturnType<typeof priceDiffs>;
        setEntranceAsk(diffs.flatMap((c) => [...(c.to !== null ? [`„${c.name}“: Katalog ${euro(c.from)}, Karte ${euro(c.to)}`] : []), ...c.variants.map((v) => `„${c.name}“, ${v.label}: Katalog ${euro(v.from)}, Karte ${euro(v.to)}`)]));
      } else toast.show(messageOf(err));
    }
  }
  /** The conflicting prices of this menu into the catalogue (then the list needs printing anew). */
  async function conflictsToCatalog() {
    if (!conflicts) return;
    try {
      await store.change(() => api("/api/prices", { method: "PUT", body: { changes: conflicts.dishes.map((d) => ({ id: d.dish, ...(d.to !== null ? { price: d.to } : {}), variants: d.variants.map((v) => ({ label: v.label, price: v.to })) })) } }));
      toast.show("Im Katalog. Der Aushang am Eingang muss neu gedruckt werden.");
    } catch (err) {
      toast.show(messageOf(err));
    }
  }
  /** This menu's conflicting prices onto the catalogue's, as at the entrance. */
  const conflictsToMenu = () => conflicts && edit((m) => withCatalogPrices(m, new Map(conflicts.dishes.map((d) => [d.dish, dishes.get(d.dish)!]))));
  const count = uniqueItems(draft);
  const counts = countsOf(draft, dishes);
  // „Nur anzeigen, was noch nicht auf der Speisekarte steht“: the picker then hides the dishes on the
  // menu, so their pencils come to the menu at the right (Martin, 25.10.2026).
  const [onlyMissing, setOnlyMissing] = useKept("pick:open", false);
  const pickerOf = (kind: DishKind) =>
    canEdit ? <Picker key={kind} kind={kind} data={data} onMenu={dishesIn(draft, scopes[kind])} scope={scopes[kind]} draft={draft} target={targetSection} setTarget={setTarget} add={add} remove={(d) => edit((m) => removeDish(m, d.id, scopes[kind]))} toast={toast.show} onlyMissing={onlyMissing} setOnlyMissing={setOnlyMissing} /> : null;
  const picker = pickerOf("food");
  const drinkPicker = drinksOn ? pickerOf("drink") : null;
  const texts = canEdit ? <TextPicker data={data} onMenu={onMenu} toggle={toggleText} toast={toast.show} menu={draft} target={targetSection} setTarget={setTarget} /> : null;
  const textCount = counts.texts;
  const panel = <MenuPanel draft={draft} draftId={opened} data={data} edit={edit} canEdit={canEdit} target={targetSection} setTarget={setTarget} wide={wide} pencils={onlyMissing && editsCatalog(role)} locked={isEntrance} />;

  const go = (to: Step) => {
    setStep(to);
    window.scrollTo({ top: 0 });
  };
  const look = <LookPanel draft={draft} data={data} edit={edit} canEdit={canEdit} designs={designs} setDesigns={setDesigns} mine={mine} id={id} lang={printLang} />;
  const pdf = <PdfPreview menu={draft} design={menuDesign} id={id} lang={printLang} />;

  return (
    <div className="editor">
      <div className="toolbar">
        <a
          className="back"
          href="#/speisekarten"
          onClick={(e) => {
            if (!anyDirty || !canEdit) return;
            if (!confirm("Die Speisekarte ist nicht gespeichert. Ohne Speichern zurück zu den Speisekarten?")) return e.preventDefault();
            drafts.delete(key);
            designDraft = null;
          }}
          data-testid="back"
        >
          ← Karten
        </a>
        <span className={`state ${anyDirty ? "dirty" : ""}`} data-testid="save-state">
          {!canEdit ? "Nur ansehen" : !base ? "Neu, nicht gespeichert" : dirty ? "Nicht gespeichert" : designsDirty ? "Stil nicht gespeichert" : published ? "Gespeichert · im Netz" : "Gespeichert"}
        </span>
        {canEdit && (
          <button className={anyDirty ? "primary" : ""} disabled={busy || (!anyDirty && Boolean(base))} onClick={() => void saveAll()} data-testid="save">
            {busy ? "Speichern …" : "Speichern"}
          </button>
        )}
      </div>

      <nav className="steps" aria-label="Schritte">
        {STEPS.map((x, i) => (
          <button key={x.label} className={step === i + 1 ? "active" : step > i + 1 ? "done" : ""} onClick={() => go((i + 1) as Step)} aria-current={step === i + 1 ? "step" : undefined} data-testid={`step-${i + 1}`}>
            <span className="no">{i + 1}</span>
            <span className="label">
              {x.label}
              <small>
                {i === 0
                  ? [`${counts.food} ${counts.food === 1 ? "Speise" : "Speisen"}`, counts.drinks ? `${counts.drinks} ${counts.drinks === 1 ? "Getränk" : "Getränke"}` : "", textCount ? `${textCount} ${textCount === 1 ? "Textbaustein" : "Textbausteine"}` : ""].filter(Boolean).join(", ")
                  : x.hint}
              </small>
            </span>
          </button>
        ))}
      </nav>

      {/* After saving (S62, Z-0): what outside still shows of the old state, with the way to renew it. */}
      {afterSave && !anyDirty && savedMenu && <OutsideFix inline menu={savedMenu} data={data} title="Gespeichert" onClose={() => setAfterSave(false)} />}

      {step === 1 && (
        <>
          {wide ? (
            <div className={`split ${picker ? "choose" : ""}`} style={{ marginTop: "1rem" }}>
              <div className={picker ? "choose-pane" : undefined}>
                {picker && (
                  <div className="segmented">
                    <button className={side === "pick" ? "active" : ""} onClick={() => setSide("pick")} data-testid="side-pick">
                      {/* Each kind says how many are on the menu, as „Getränke“ and „Textbausteine“ do (Martin, 25.10.2026). */}
                      Speisen{counts.food ? ` · ${counts.food}` : ""}
                    </button>
                    {drinkPicker && (
                      <button className={side === "drinks" ? "active" : ""} onClick={() => setSide("drinks")} data-testid="side-drinks">
                        Getränke{counts.drinks ? ` · ${counts.drinks}` : ""}
                      </button>
                    )}
                    <button className={side === "texts" ? "active" : ""} onClick={() => setSide("texts")} data-testid="side-texts">
                      Textbausteine{textCount ? ` · ${textCount}` : ""}
                    </button>
                  </div>
                )}
                {picker ? (side === "texts" ? texts : side === "drinks" && drinkPicker ? drinkPicker : picker) : panel}
              </div>
              <div>{picker ? panel : null}</div>
            </div>
          ) : (
            <>
              {canEdit && (
                <div className={`segmented ${drinkPicker ? "four" : ""}`} style={{ marginTop: "0.8rem" }}>
                  <button className={tab === "pick" ? "active" : ""} onClick={() => setTab("pick")} data-testid="tab-pick">
                    Speisen{counts.food ? <span className="n">{counts.food}</span> : null}
                  </button>
                  {drinkPicker && (
                    <button className={tab === "drinks" ? "active" : ""} onClick={() => setTab("drinks")} data-testid="tab-drinks">
                      Getränke{counts.drinks ? <span className="n">{counts.drinks}</span> : null}
                    </button>
                  )}
                  <button className={tab === "texts" ? "active" : ""} onClick={() => setTab("texts")} title="Textbausteine" data-testid="tab-texts">
                    {/* Four tabs on a phone: short, as „Karte“ beside it (S61 § 11). */}
                    {drinkPicker ? "Bausteine" : "Textbausteine"}
                    {textCount ? <span className="n">{textCount}</span> : null}
                  </button>
                  <button className={tab === "menu" ? "active" : ""} onClick={() => setTab("menu")} data-testid="tab-menu">
                    {drinkPicker ? "Karte" : "Auf der Karte"}
                    <span className="n">{count}</span>
                  </button>
                </div>
              )}
              {tab === "pick" && picker ? picker : tab === "drinks" && drinkPicker ? drinkPicker : tab === "texts" && texts ? texts : panel}
            </>
          )}
        </>
      )}

      {step === 2 && (
        <div className="step-split">
          <div>
            <h2 className="step-title">{draft.title}</h2>
            {langBlock}
            {look}
            <DesignPanel design={menuDesign} card={isNoScale(draft.scale) ? null : { design: scaleDesign(menuDesign, draft.scale, draft.layout === "columns" || draft.drinks?.layout === "columns"), says: scaleSummary(draft.scale!) }} change={changeDesign} usePreset={usePreset} open={openStyle} setOpen={setOpenStyle} canEdit={canEdit && editsCatalog(role)} style={designs.list.find((x) => x.id === mine)} styles={designs.list} sections={draft.structure === "sections" || (hasDrinks(draft) && draft.drinks?.structure === "sections")} numbers={draft.showNumbers || (hasDrinks(draft) && Boolean(draft.drinks?.showNumbers)) || draft.sections.some((x) => x.kind === "set" && x.numbers && x.items.some((i) => i.or))} drinks={hasDrinks(draft)} setDrinks={draft.sections.some((x) => x.kind === "set" && x.items.some((i) => isDrink(dishes.get(i.dish) ?? {})))} sets={draft.sections.some((x) => x.kind === "set" && x.items.length > 0)} subsections={draft.structure === "sections" && draft.sections.some((x) => x.kind !== "set" && x.items.some((i) => i.sub))} heads={hasDrinks(draft) || (draft.structure === "sections" && draft.sections.some((x) => x.kind !== "set" && x.part !== "drinks" && Boolean(x.priceLabel)))} bilingual={Boolean(printLang?.both)} />
          </div>
          <div className="design-preview">
            <PdfPreview menu={draft} design={menuDesign} id={id} open={openStyle} onPick={pickStyle} lang={printLang} />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="step-split">
          <div className="out">
            <h2 className="step-title">{draft.title}</h2>
            {canEdit && anyDirty && (
              <p className="warn">
                Noch nicht gespeichert. Drucken und PDF gehen trotzdem; zum Veröffentlichen wird gespeichert.{" "}
                <button className="link inline" onClick={() => void saveAll()}>
                  Jetzt speichern
                </button>
              </p>
            )}
            {langChips}
            {missingText && (
              <p className="hint" data-testid="print-lang-open">
                {missingText}{" "}
                <button className="link inline" onClick={() => go(2)} data-testid="to-translate">
                  Übersetzen unter „2 Aussehen“
                </button>
              </p>
            )}
            {printLang && toLook > 0 && savedId && (
              // Decided (S61): the model's translation is printed, but stays to be looked through, visibly.
              <p className="hint" data-testid="print-lang-review">
                {LANG_LABELS[printLang.lang]}: {textsCount(toLook)} nicht durchgesehen.{" "}
                <a href={reviewHref(savedId, { lang: printLang.lang, flow: model })}>{textsAction(data, model, cardStatus)}</a>
              </p>
            )}
            <div className="out-actions">
              <button className="primary" onClick={() => void outputs(() => printMenu(pm(), data))} data-testid="print">
                <b>Drucken</b>
                <span>auf dem Drucker, so wie im PDF</span>
              </button>
              <button onClick={() => void outputs(() => downloadPdf(pm(), data))} data-testid="pdf">
                <b>PDF herunterladen</b>
                <span>zum Versenden, für die Druckerei</span>
              </button>
              <button onClick={() => void outputs(() => downloadWord(pm(), data))} data-testid="word">
                <b>Word-Datei</b>
                <span>zum Weiterbearbeiten in Word</span>
              </button>
              <button onClick={() => setPreview(true)} data-testid="preview">
                <b>Druckansicht</b>
                <span>die Seite, wie der Drucker sie bekommt</span>
              </button>
            </div>
            <OwnPagesChoice menu={draft} data={data} paper={pageOf(menuDesign.page.format)} insert={isInsert(data, id)} canEdit={canEdit} onChange={(ownPages) => edit((m) => { const { ownPages: _, ...rest } = m; return ownPages ? { ...rest, ownPages } : rest; })} />
            {publishes(role) && (
              <div className="card out-web">
                <h3>QR-Code-Speisekarte</h3>
                <p className="hint">
                  {published
                    ? anyDirty || webOld
                      ? "Das ist die QR-Code-Speisekarte; Gäste sehen noch den alten Stand."
                      : "Das ist die QR-Code-Speisekarte. Gäste sehen sie über den QR-Code."
                    : qrPlanned
                      ? `Geplant: ${whenText(qrPlanned.at, Date.now())} als QR-Code-Speisekarte, mit allen Änderungen bis dahin. Bis dahin sehen Gäste ${data.published ? `„${data.menus.find((m) => m.id === data.published!.menu)?.title ?? "eine andere Speisekarte"}“` : "keine Speisekarte"}.`
                      : data.published
                        ? `Gäste sehen zurzeit „${data.menus.find((m) => m.id === data.published!.menu)?.title ?? "eine andere Speisekarte"}“. Veröffentlichen ersetzt sie, der QR-Code bleibt.`
                        : "Gäste sehen zurzeit keine Speisekarte. Nach dem Veröffentlichen gibt es QR-Code und Aufkleber."}
                </p>
                <NetLangs data={data} menu={{ ...draft, id }} now={Boolean(published) && !anyDirty && !webOld} />
                {/* Der Allergenfilter (S63, A7, Bild 6): what lacks its allergens, before publishing. */}
                <FilterMissing data={data} menus={[draft]} which="dieser Karte" />
                {timed && !published && !qrPlanned && <WhenChoice what="menu" value={qrWhen} onChange={setQrWhen} testid="publish-when" />}
                <div className="buttons">
                  {timed && !published && qrWhen.kind !== "now" ? (
                    <button className="primary" onClick={() => void plan("menu", qrWhen)} disabled={busy || count === 0} data-testid="publish-plan">
                      Einplanen
                    </button>
                  ) : (
                    <button className={(published && !anyDirty && !webOld) || (!published && qrPlanned) ? "" : "primary"} onClick={() => void publish()} disabled={busy || count === 0 || (Boolean(published) && !anyDirty && !webOld)} data-testid="publish">
                      {published ? (anyDirty || webOld ? "Änderungen übernehmen" : "Ist veröffentlicht") : qrPlanned ? "Sofort veröffentlichen" : "Veröffentlichen"}
                    </button>
                  )}
                  <a className="button" href="#/netz">
                    QR-Code und Aufkleber …
                  </a>
                </div>
                {timed && base && <PlannedList data={data} what="menu" menu={base.id} may={publishes(role)} />}
              </div>
            )}
            {publishes(role) && (data.pdfSlots?.length ?? 0) > 0 && <PdfSlotOut data={data} pm={pm} lang={printLang} id={id} disabled={busy || count === 0} outputs={outputs} save={async () => (anyDirty || !base ? await saveAll() : base.id)} />}
            {recommends && (
              <div className="card out-web" data-testid="as-recommendation">
                <h3>Als Empfehlungskarte</h3>
                <p className="hint">
                  {isRecommendation
                    ? anyDirty || recOld
                      ? "Ist die Empfehlungskarte; Gäste sehen noch den alten Stand."
                      : `Ist die Empfehlungskarte; der QR-Code zeigt sie vor „${data.menus.find((m) => m.id === data.published!.menu)?.title ?? "der QR-Code-Speisekarte"}“.`
                    : recPlanned
                      ? `Geplant: ${whenText(recPlanned.at, Date.now())} als Empfehlungskarte, mit allen Änderungen bis dahin.`
                      : `Der QR-Code zeigt die Empfehlungskarte vor der QR-Code-Speisekarte „${data.menus.find((m) => m.id === data.published!.menu)?.title ?? ""}“. Gedruckt wird sie als Einlegeblatt.`}
                </p>
                <FilterMissing data={data} menus={[draft]} which="dieser Karte" />
                {timed && !isRecommendation && !recPlanned && <WhenChoice what="recommendations" value={recWhen} onChange={setRecWhen} testid="recommendation-when" />}
                <div className="buttons">
                  {timed && !isRecommendation && recWhen.kind !== "now" ? (
                    <button className="primary" onClick={() => void plan("recommendations", recWhen)} disabled={busy || count === 0} data-testid="recommendation-plan">
                      Einplanen
                    </button>
                  ) : (
                    <button className={(isRecommendation && !anyDirty && !recOld) || (!isRecommendation && recPlanned) ? "" : "primary"} onClick={() => void asRecommendation()} disabled={busy || count === 0 || (isRecommendation && !anyDirty && !recOld)} data-testid="as-recommendation-button">
                      {isRecommendation ? (anyDirty || recOld ? "Änderungen übernehmen" : "Ist die Empfehlungskarte") : recPlanned ? "Sofort als Empfehlungskarte zeigen" : "Als Empfehlungskarte zeigen"}
                    </button>
                  )}
                </div>
                {timed && base && <PlannedList data={data} what="recommendations" menu={base.id} may />}
              </div>
            )}
            {conflictLines.length > 0 && (
              <div className="card out-web" data-testid="entrance-conflicts">
                <h3>Anderer Preis als am Eingang</h3>
                <p className="warn">So geht's nicht: Am Eingang hängt {conflictLines.join("; ")}.</p>
                <div className="buttons">
                  <button onClick={conflictsToMenu} disabled={!canEdit} data-testid="conflicts-to-menu">
                    Preise wie am Eingang
                  </button>
                  {editsCatalog(role) && conflicts!.dishes.length > 0 && (
                    <button onClick={() => void conflictsToCatalog()} data-testid="conflicts-to-catalog">
                      Diese Preise in den Katalog
                    </button>
                  )}
                </div>
                <p className="hint">In den Katalog: Dann gelten sie auch am Eingang, und der Aushang muss neu gedruckt werden.</p>
              </div>
            )}
            {canEdit && editsCatalog(role) && (
              <div className="card out-web" data-testid="as-entrance">
                <h3>Aushang am Eingang</h3>
                {isEntrance ? (
                  <>
                    <p className="hint">Hängt am Eingang aus (§ 13 Abs. 2 PAngV). Die Preise kommen aus dem Katalog.</p>
                    <p className={printedOld ? "warn" : "hint"} data-testid="entrance-printed">
                      {printedOld ? (data.settings.entrancePrinted ? "Seit dem letzten Druck geändert: bitte neu drucken und aushängen." : "Noch nicht gedruckt: bitte drucken und aushängen.") : `Zuletzt gedruckt am ${new Date(data.settings.entrancePrinted!.at).toLocaleDateString("de-DE")}.`}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="hint">Neben dem Eingang muss ein Preisverzeichnis der wesentlichen Speisen und Getränke hängen (§ 13 Abs. 2 PAngV). Als Aushang kommen die Preise dieser Speisekarte aus dem Katalog.{entranceMenu ? ` Bisher hängt „${entranceMenu.title}“ aus.` : ""}</p>
                    {entranceAsk ? (
                      <div data-testid="entrance-ask">
                        <p className="warn">Auf dieser Speisekarte stehen andere Preise als im Katalog: {entranceAsk.join("; ")}.</p>
                        <div className="buttons">
                          <button className="primary" onClick={() => void asEntrance("catalog")} data-testid="entrance-catalog">
                            Preise des Katalogs nehmen
                          </button>
                          <button onClick={() => void asEntrance("menu")} data-testid="entrance-menu">
                            Katalog wie auf der Karte
                          </button>
                          <button onClick={() => setEntranceAsk(null)} data-testid="entrance-cancel">
                            Nicht als Aushang
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                      <div className="buttons">
                        <button onClick={() => void asEntrance()} disabled={busy || count === 0} data-testid="as-entrance-button">
                          Als Aushang am Eingang
                        </button>
                        {base && (
                          <a className="button" href={`#/speisekarte/neu?von=${base.id}&aushang=1`} data-testid="new-entrance">
                            Neuer Aushang als Auszug
                          </a>
                        )}
                      </div>
                      <p className="hint">„Neuer Aushang als Auszug“: eine Kopie dieser Speisekarte mit dem Titel „{ENTRANCE_TITLE}“, zum Kürzen auf das Wesentliche.</p>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="design-preview">{pdf}</div>
        </div>
      )}

      <div className="step-nav">
        {step > 1 ? (
          <button onClick={() => go((step - 1) as Step)} data-testid="step-back">
            ← {STEPS[step - 2]!.label}
          </button>
        ) : (
          <span />
        )}
        {step < 3 && (
          <button className="primary" onClick={() => go((step + 1) as Step)} data-testid="step-next">
            Weiter: {STEPS[step]!.label} →
          </button>
        )}
      </div>

      {diffs && <PriceDialog diffs={diffs} onClose={(msg) => [setDiffs(null), msg && toast.show(msg)]} />}
      {preview && <Preview menu={draft} design={menuDesign} lang={printLang} onClose={() => setPreview(false)} />}
      <Toast toast={toast} />
    </div>
  );
}

// ---------------------------------------------------------------------------

/** The words for what a picker picks (S22). */
const WORDS: Record<DishKind, { one: string; many: string }> = { food: { one: "Speise", many: "Speisen" }, drink: { one: "Getränk", many: "Getränke" } };

function Picker(props: { kind: DishKind; data: Data; draft: MenuInput; onMenu: Set<string>; scope: Scope; target: string | undefined; setTarget: (id: string | null) => void; add: (d: Dish[], ors?: ReadonlySet<string>) => number; remove: (d: Dish) => void; toast: (m: string) => void; onlyMissing: boolean; setOnlyMissing: (v: boolean) => void }) {
  const { data, onMenu, kind, scope } = props;
  const drink = kind === "drink";
  // „Auf der Speisekarte“ or „im Menü …“: a tap puts it into the set menu chosen (C1).
  const setTitle = scope === null ? null : props.draft.sections.find((s) => s.id === scope)?.title || "Menü";
  const where = setTitle === null ? "auf der Speisekarte" : `im Menü „${setTitle}“`;
  const w = WORDS[kind];
  const n = (k: number) => (k === 1 ? `1 ${w.one}` : `${k} ${w.many}`);
  const key = drink ? "pick:drink" : "pick";
  // Search and filters stay while one looks elsewhere (S17), the same for every menu.
  const [q, setQ] = useKept(`${key}:q`, "");
  const [cat, setCat] = useKept<string | null>(`${key}:cat`, null);
  const [tag, setTag] = useKept<string | null>(`${key}:tag`, null);
  const [open, setOpen] = [props.onlyMissing, props.setOnlyMissing];
  const [showTags, setShowTags] = useKept(`${key}:tags`, false);
  const input = useRef<HTMLInputElement>(null);
  const all = useMemo(() => data.dishes.filter((d) => !d.archived), [data.dishes]);
  const active = useMemo(() => all.filter((d) => kindOf(d) === kind), [all, kind]);
  const order = drink ? data.drinkCategories : data.categories;
  const tagList = drink ? data.drinkTags : data.tags;
  const categories = useMemo(() => allCategories(active, order), [active, order]);
  // The listed tags (S17) that dishes have, in the list's order.
  const tags = useMemo(() => {
    const used = new Set(active.flatMap((d) => d.tags).map(tagKey));
    return tagList.filter((t) => used.has(tagKey(t))).slice(0, 14);
  }, [active, tagList]);
  // „21/22 30“: a slash is „oder“ in a set menu (S32), elsewhere as a space.
  const groups = parseNumberGroups(q);
  const numbers = groups?.flat() ?? null;
  const intoSet = scope !== null;
  // Numbers are one set over dishes and drinks (S22): typed in either list, each goes into its part.
  const byNumber = useMemo(() => new Map(all.map((d) => [d.number, d])), [all]);
  const part: Part = drink ? "drinks" : "food";
  // A set menu of the food takes drinks too (S52): it is a place for them as well.
  const partSections = props.draft.sections.filter((s) => partOf(s) === part || (drink && s.kind === "set"));
  const sectioned = partMenu(props.draft, part).structure === "sections" || (drink && partSections.some((s) => s.kind === "set"));
  const hits = numbers
    ? numbers.map((n) => byNumber.get(n)).filter((d): d is Dish => d !== undefined)
    : findDishes(active, q).filter((d) => (!cat || topCategory(d.category) === cat) && (!tag || d.tags.some((t) => t.toLowerCase() === tag.toLowerCase())) && (!open || !onMenu.has(d.id)));

  function enter(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (numbers) {
      const missing = numbers.filter((n) => !byNumber.has(n));
      const ors = new Set(groups!.flatMap((g) => g.slice(1)).flatMap((n) => byNumber.get(n)?.id ?? []));
      const added = props.add(hits, ors);
      props.toast(`${hits.some((d) => kindOf(d) !== kind) ? `${added} aufgenommen` : `${n(added)} aufgenommen`}${missing.length ? `; keine Nummer ${missing.join(", ")}` : ""}`);
      setQ("");
    } else if (hits[0]) {
      if (onMenu.has(hits[0].id)) props.toast(`„${hits[0].name}“ steht schon ${where}`);
      else props.add([hits[0]]);
      setQ("");
    }
  }

  const filtered = q.trim() !== "" || cat !== null || tag !== null;
  const notOn = hits.filter((d) => !onMenu.has(d.id));
  return (
    <div className="picker">
      <p className="hint pick-lead" data-testid="pick-lead">{setTitle === null ? "Antippen, was auf die Speisekarte soll" : `Antippen, was ins Menü „${setTitle}“ soll`}; noch einmal antippen nimmt es wieder weg.</p>
      <input ref={input} className="search" type="search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={enter} placeholder={intoSet ? "Suchen oder Nummern: 21/22 30" : "Suchen oder Nummern: 1 4 8-12"} aria-label={`${w.many} suchen oder Nummern eintippen`} enterKeyHint="done" autoComplete="off" data-testid={drink ? "pick-drink-search" : "pick-search"} />
      <p className="hint quick-hint">
        {numbers ? `Enter übernimmt ${hits.some((d) => kindOf(d) !== kind) ? `${hits.length} Einträge` : n(hits.length)} in die Speisekarte.` : "Enter übernimmt den ersten Suchtreffer in die Speisekarte."}
        {intoSet && !drink ? " Im Menü heißt / „oder“: 21/22 sind zwei Speisen zur Wahl in einem Gang." : ""}
      </p>
      {sectioned && (partSections.length > 1 || (drink && partSections.some((s) => s.kind === "set"))) && (
        <label className="row" style={{ marginBottom: 0 }}>
          <span style={{ flex: "none" }}>Aufnehmen in</span>
          <select value={partSections.some((s) => s.id === props.target) ? props.target : (drink ? "" : partSections[partSections.length - 1]?.id)} onChange={(e) => props.setTarget(e.target.value || null)} data-testid="pick-target">
            {drink && <option value="">nach Kategorie</option>}
            {partSections.map((s, i) => (
              <option key={s.id} value={s.id}>
                {s.title || (s.kind === "set" ? "Menü" : `Abschnitt ${i + 1}`)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="filters">
        <div className="chips">
          {categories.map((c) => (
            <button key={c} className={cat === c ? "active" : ""} onClick={() => setCat(cat === c ? null : c)}>
              {c}
            </button>
          ))}
          {showTags || tag
            ? tags.map((t) => (
                <button key={t} className={tag === t ? "active" : ""} onClick={() => setTag(tag === t ? null : t)}>
                  #{t}
                </button>
              ))
            : tags.length > 0 && (
                <button onClick={() => setShowTags(true)} data-testid="show-tags">
                  # Tags …
                </button>
              )}
        </div>
      </div>
      <div className="count">
        <label className="check" style={{ margin: 0, fontSize: "0.85rem" }}>
          <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} data-testid="pick-missing" />
          nur anzeigen, was noch nicht {where} steht
        </label>
        {filtered && notOn.length > 1 && (
          <button className="link" onClick={() => props.toast(`${n(props.add(notOn))} aufgenommen`)} data-testid="pick-all">
            Alle {notOn.length} aufnehmen
          </button>
        )}
      </div>
      <ul className="list results">
        {hits.map((d) => {
          const on = onMenu.has(d.id);
          const also = placesOf(props.draft, d.id, scope);
          return (
            <li key={d.id} className="pick-item">
              <button className={`dish-row ${on ? "on" : ""}`} onClick={() => (on ? props.remove(d) : props.add([d]))} aria-pressed={on} data-testid={`pick-${d.number}`}>
                <span className="no">{d.number}</span>
                <span className="name">
                  <b>{d.name}</b>
                  {d.allergens?.length ? <sup className="codes">{d.allergens.join(",")}</sup> : null}
                  <span className="facts">{[d.category, d.tags.join(", ")].filter(Boolean).join(" · ")}</span>
                  {also.length > 0 && (
                    <span className="facts also" data-testid={`also-${d.number}`}>
                      {`auch ${also.map((x) => (x === "Karte" ? "auf der Karte" : `in „${x}“`)).join(", ")}`}
                    </span>
                  )}
                </span>
                <span className="price">{plainPrice(d.price)}</span>
              </button>
              {editsCatalog(store.role) && (
                <a className="edit" href={`${isDrink(d) ? "#/getraenk/" : "#/speise/"}${d.id}`} onClick={rememberHere} aria-label={`${d.name} bearbeiten`} title="Im Katalog bearbeiten" data-testid={`edit-${d.number}`}>
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" /></svg>
                </a>
              )}
            </li>
          );
        })}
      </ul>
      {hits.length === 0 && <p className="empty">{active.length === 0 ? (drink ? "Noch keine Getränke. Unter „Kataloge“ → „Getränke“ anlegen oder eine Excel-Mappe hochladen." : "Noch keine Speisen. Unter „Kataloge“ anlegen oder eine Excel-Mappe hochladen.") : "Nichts gefunden."}</p>}
    </div>
  );
}

/**
 * „Aufnehmen in“ over both parts (S22): every section of the food and of the drinks, the food's list
 * as „Speisen“, the drinks' as „Getränke“.
 */
function SectionChoice({ draft, target, setTarget }: { draft: MenuInput; target: string | undefined; setTarget: (id: string) => void }) {
  const label = (s: MenuSection, i: number, part: Part) => {
    const sub = partMenu(draft, part);
    const own = s.title || (s.kind === "set" ? "Menü" : sub.structure === "list" ? "" : `Abschnitt ${i + 1}`);
    const name = part === "drinks" ? "Getränke" : "Speisen";
    return own ? `${name} · ${own}` : name;
  };
  return (
    <label className="row" style={{ marginBottom: 0 }}>
      <span style={{ flex: "none" }}>Aufnehmen in</span>
      <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="text-target">
        {(["food", "drinks"] as const).flatMap((part) =>
          partMenu(draft, part).sections.map((s, i) => (
            <option key={s.id} value={s.id}>
              {label(s, i, part)}
            </option>
          )),
        )}
      </select>
    </label>
  );
}

/** The pane „Textbausteine“ (S14): the texts of the catalogue to put on the menu, and a new one right here. */
function TextPicker({ data, onMenu, toggle, toast, menu, target, setTarget }: { data: Data; onMenu: Set<string>; toggle: (t: CardText) => void; toast: (m: string) => void; menu: MenuInput; target: string | undefined; setTarget: (id: string) => void }) {
  const [draft, setDraft] = useKept("texts:new", { title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const canEdit = editsCatalog(store.data?.role ?? "viewer");
  async function create() {
    const fresh: CardText = { id: uuidv7(), title: draft.title.trim(), body: draft.body.trim() };
    if (!fresh.title && !fresh.body) return toast("Bitte eine Überschrift oder einen Text angeben.");
    setBusy(true);
    try {
      await store.change(() => api("/api/texts", { method: "PUT", body: { texts: [...data.texts, fresh] } }));
      toggle(fresh);
      setDraft({ title: "", body: "" });
      toast("Text angelegt und aufgenommen");
    } catch (err) {
      toast(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="picker text-picker">
      <p className="hint pick-lead" data-testid="pick-lead">Antippen, was auf die Speisekarte soll; noch einmal antippen nimmt es wieder weg.</p>
      <p className="hint quick-hint">Zeilen ohne Nummer und Preis, etwa „Tapa del día“; sie kommen ans Ende des Abschnitts und lassen sich in der Speisekarte am Anfasser verschieben. Pflegen unter Kataloge → <a href="#/textbausteine">Textbausteine</a>.</p>
      {/* With drinks (S22) a text may go to the food or to any section of the drinks: chosen here, not by chance. */}
      {menu.sections.some((s) => partOf(s) === "drinks") && <SectionChoice draft={menu} target={target} setTarget={setTarget} />}
      <ul className="list results">
        {data.texts.map((t) => {
          const on = onMenu.has(t.id);
          return (
            <li key={t.id} className="pick-item">
              <button className={`dish-row ${on ? "on" : ""}`} onClick={() => toggle(t)} aria-pressed={on} data-testid="pick-text">
                <span className="no" aria-hidden="true">¶</span>
                <span className="name">
                  <b>{textLabel(t)}</b>
                  {t.title && t.body ? <span className="facts">{t.body}</span> : null}
                </span>
                <span />
              </button>
              {editsCatalog(store.role) && (
                <a className="edit" href={`#/textbausteine/${t.id}`} onClick={rememberHere} aria-label={`${textLabel(t)} bearbeiten`} title="Textbaustein bearbeiten" data-testid="edit-text">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" /></svg>
                </a>
              )}
            </li>
          );
        })}
      </ul>
      {data.texts.length === 0 && <p className="empty">Noch keine Textbausteine.</p>}
      {canEdit && (
        <div className="text-new">
          <b>Neuer Textbaustein</b>
          <SymbolInput value={draft.title} onValue={(v) => setDraft({ ...draft, title: v })} placeholder="Überschrift, z. B. Tapa del día" aria-label="Überschrift des neuen Textbausteins" data-testid="new-text-title" />
          <SymbolInput value={draft.body} onValue={(v) => setDraft({ ...draft, body: v })} placeholder="Text (wahlweise)" aria-label="Text des neuen Textbausteins" data-testid="new-text-body" />
          <button disabled={busy} onClick={() => void create()} data-testid="new-text-add">
            Anlegen und aufnehmen
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * „Das steht auf der Speisekarte“ (Martin, 24.10.2026): the menu from top to bottom as it is printed
 * – head (title, subtitle), the dishes and texts, the foot (the note at the bottom) – under a bar for
 * its build (list or sections, sorting); below it, as entries to open, what else the menu shows
 * (numbers, tags). Empty, it says where to begin. With drinks (S22) their part follows the food's,
 * under the heading „Getränke“ with a bar of its own.
 */
/** A correction taken into a menu's draft (S47), at the spot it was found. */
function withSpotTaken(m: MenuInput, id: string, e: OpenEdit): MenuInput {
  const spot = menuSpots(id, m).find((s) => s.key === e.key);
  return spot?.at ? withMenuText(m, spot.at, applyProofEdit(spot.text, e.edit)) : m;
}

function MenuPanel({ draft, draftId, data, edit, canEdit, target, setTarget, wide, pencils, locked = false }: { draft: MenuInput; draftId: string; data: Data; edit: (fn: (m: MenuInput) => MenuInput) => void; canEdit: boolean; target: string | undefined; setTarget: (id: string) => void; wide: boolean; pencils: boolean; locked?: boolean }) {
  const [more, setMore] = useKept<string | null>(`${draftId}:more`, null);
  const count = itemCount(draft);
  const dishes = dishesById(data);
  const counts = countsOf(draft, dishes);
  const names = new Map([...[...dishes].map(([id, d]) => [id, d.name] as const), ...data.texts.map((t) => [t.id, t.title || t.body.slice(0, 40)] as const)]);
  const textCount = counts.texts;
  const dishCount = counts.food;
  const sections = draft.structure === "sections";
  const drinksPart = draft.sections.some((s) => partOf(s) === "drinks");
  // The tags of the dishes and drinks on this menu, in the order of the lists (S17, S22).
  const onCard = new Set(draft.sections.flatMap((s) => s.items).flatMap((i) => dishes.get(i.dish)?.tags ?? []).map(tagKey));
  const allTags = [...data.tags, ...data.drinkTags.filter((t) => !data.tags.some((x) => tagKey(x) === tagKey(t)))];
  const tagsUsed = allTags.filter((t) => onCard.has(tagKey(t)));
  const shown = draft.showTags ?? data.tags.filter(isDietTag).map((t) => t.toLowerCase());
  const sort = useSortable(
    (from, to) => edit((m) => moveItem(m, { section: from.group, index: from.index }, { section: to.group, index: to.index }, dishes)),
    (from: Slot, by) =>
      edit((m) => {
        const si = m.sections.findIndex((x) => x.id === from.group);
        const sec = m.sections[si];
        if (!sec) return m;
        const at = { section: from.group, index: from.index };
        if (by < 0) return from.index > 0 ? moveItem(m, at, { section: sec.id, index: from.index - 1 }, dishes) : si > 0 ? moveItem(m, at, { section: m.sections[si - 1]!.id, index: m.sections[si - 1]!.items.length }, dishes) : m;
        return from.index < sec.items.length - 1 ? moveItem(m, at, { section: sec.id, index: from.index + 1 }, dishes) : si < m.sections.length - 1 ? moveItem(m, at, { section: m.sections[si + 1]!.id, index: 0 }, dishes) : m;
      }),
  );

  // On an empty menu all tags of the list, to choose before the first dish; then those of its dishes.
  const offered = tagsUsed.length ? tagsUsed : data.tags;
  const shownTags = offered.filter((t) => shown.includes(t.toLowerCase()));
  const toggle = (id: string) => setMore(more === id ? null : id);
  const part = (which: Part) => <PartSections part={which} draft={draft} data={data} edit={edit} canEdit={canEdit} target={target} setTarget={setTarget} wide={wide} pencils={pencils} sort={sort} locked={locked} />;

  return (
    <div className="menu-panel">
      <h2 className="panel-title" data-testid="panel-title">
        {count === 0 && canEdit ? (
          wide ? "← Wähle links aus, was auf die Speisekarte soll" : drinksInPlay(data) ? "Wähle unter „Speisen“, „Getränke“ und „Textbausteine“ aus, was auf die Speisekarte soll" : "Wähle unter „Speisen“ und „Textbausteine“ aus, was auf die Speisekarte soll"
        ) : (
          <>
            Das steht auf der Speisekarte
            <small>
              {[dishCount ? `${dishCount} ${dishCount === 1 ? "Speise" : "Speisen"}` : "", counts.drinks ? `${counts.drinks} ${counts.drinks === 1 ? "Getränk" : "Getränke"}` : "", textCount ? `${textCount} ${textCount === 1 ? "Textbaustein" : "Textbausteine"}` : ""].filter(Boolean).join(", ")}
            </small>
          </>
        )}
      </h2>

      {/* The build of the menu: list or sections, sorting – a bar above it. */}
      {canEdit && <PartBar part="food" draft={draft} data={data} edit={edit} />}

      {/* „Deutsch prüfen“ (S47): the corrections open in this menu's own texts as typed, taken into the draft. */}
      <ProofBox edits={openEdits(data, menuSpots(draftId, draft, names))} take={(e) => edit((m) => withSpotTaken(m, draftId, e))} canEdit={canEdit} />

      {/* The menu as printed: the head … One root for dragging, so a text can go from the food to the drinks (S22). */}
      <div className="menu-sheet" data-sort-root>
        <div className="menu-head">
          <input className="title" value={draft.title} onChange={(e) => edit((m) => ({ ...m, title: e.target.value }))} placeholder="Titel der Speisekarte" aria-label="Titel der Speisekarte" readOnly={!canEdit} data-testid="menu-title" />
          <SymbolInput value={draft.subtitle} onValue={(v) => edit((m) => ({ ...m, subtitle: v }))} placeholder="Untertitel, z. B. Herbst 2026" aria-label="Untertitel" readOnly={!canEdit} />
        </div>

        {/* An empty list: a row as large as a dish's, saying where the dishes will stand. */}
        {count === 0 && !sections && canEdit && (
          <div className="card section-card placeholder" data-testid="menu-placeholder">
            <div className="item">
              <span className="name">
                {wide ? "Hier stehen die Speisen, sobald sie links angetippt werden." : "Hier stehen die Speisen, sobald sie unter „Speisen“ angetippt werden."}
                <span className="facts">&nbsp;</span>
              </span>
              {/* Unseen, so the row is exactly as high as a dish's. */}
              <span className="moves" aria-hidden="true">
                <button tabIndex={-1}>{" "}</button>
                <span className="grip" />
              </span>
            </div>
          </div>
        )}
        {/* A menu of drinks alone (S22) shows no empty part of food. */}
        {!(count === 0 && !sections && canEdit) && !(drinksPart && !sections && partMenu(draft, "food").sections.every((x) => x.items.length === 0)) && part("food")}
        {/* … the drinks (S22) … */}
        {drinksPart && (
          <div className="drinks-part" data-testid="drinks-part">
            <div className="part-head">
              <h3>Getränke</h3>
              {canEdit && (
                <button
                  className="link danger"
                  onClick={() => {
                    if (counts.drinks && !confirm("Die Getränke von dieser Speisekarte nehmen?")) return;
                    edit((m) => {
                      const next = { ...m, sections: m.sections.filter((x) => partOf(x) === "food") };
                      delete next.drinks;
                      return next;
                    });
                  }}
                  aria-label="Getränke von der Speisekarte nehmen"
                  data-testid="drinks-remove"
                >
                  ✕
                </button>
              )}
            </div>
            {canEdit && <PartBar part="drinks" draft={draft} data={data} edit={edit} />}
            {part("drinks")}
          </div>
        )}
        {/* … and the foot. */}
        {canEdit && (
          <label className="menu-note">
            Hinweis unten auf der Speisekarte
            <SymbolTextarea rows={2} aria-label="Hinweis unten auf der Speisekarte" value={draft.note} onValue={(v) => edit((m) => ({ ...m, note: v }))} placeholder="z. B. Alle Tapas auch zum Teilen" data-testid="menu-note" />
          </label>
        )}
      </div>

      {/* What else the menu shows, as in step 2: entries to open, the dot, the value when closed. */}
      {canEdit && (
        <div className="styles-list menu-more">
          <PartRow id="numbers" label="Nummern der Speisen" summary={draft.showNumbers ? "an" : "aus"} open={more === "numbers"} onOpen={() => toggle("numbers")}>
            <label className="check">
              <input type="checkbox" checked={draft.showNumbers} onChange={(e) => edit((m) => ({ ...m, showNumbers: e.target.checked }))} data-testid="show-numbers" />
              Die Nummer vor jeder Speise („die 12 bitte“)
            </label>
          </PartRow>
          {drinksPart && draft.drinks && (
            <PartRow id="drinkNumbers" label="Nummern der Getränke" summary={draft.drinks.showNumbers ? "an" : "aus"} open={more === "drinkNumbers"} onOpen={() => toggle("drinkNumbers")}>
              <label className="check">
                <input type="checkbox" checked={draft.drinks.showNumbers} onChange={(e) => edit((m) => ({ ...m, drinks: { ...(m.drinks as DrinksPart), showNumbers: e.target.checked } }))} data-testid="show-drink-numbers" />
                Die Nummer vor jedem Getränk
              </label>
            </PartRow>
          )}
          <PartRow id="tags" label="Tags auf der Speisekarte" summary={<FitList items={shownTags} />} open={more === "tags"} onOpen={() => toggle("tags")}>
            {offered.length === 0 && (
              <p className="hint">
                Noch keine Tags im Katalog. <a href="#/tags">Tags anlegen …</a>
              </p>
            )}
            <div className="chips" data-testid="menu-tags">
              {offered.map((t) => {
                const on = shown.includes(t.toLowerCase());
                return (
                  <button key={t} className={on ? "active" : ""} aria-pressed={on} onClick={() => edit((m) => ({ ...m, showTags: on ? shown.filter((x) => x !== t.toLowerCase()) : [...shown, t.toLowerCase()] }))}>
                    {data.tagIcons[t] && <TagSymbol id={data.tagIcons[t]!} size={15} />} {t}
                  </button>
                );
              })}
            </div>
            <p className="hint">Gewählte Tags stehen bei den Speisen, die sie haben.</p>
          </PartRow>
        </div>
      )}
    </div>
  );
}

/**
 * The bar of a part: list or sections, sections from categories, sorting, and what is missing
 * (allergens; for drinks the amounts and the rule of § 6 GastG, S22).
 */
function PartBar({ part, draft, data, edit }: { part: Part; draft: MenuInput; data: Data; edit: (fn: (m: MenuInput) => MenuInput) => void }) {
  const dishes = dishesById(data);
  const sub = partMenu(draft, part);
  const sections = sub.structure === "sections";
  const items = sub.sections.flatMap((s) => s.items);
  const count = items.length;
  const order = part === "drinks" ? data.drinkCategories : data.categories;
  const missing = items.filter((i) => dishes.get(i.dish)?.allergens === null).length;
  const guessed = guessedOn(sub, dishes);
  const drinks = part === "drinks";
  const amounts = drinks ? items.filter((i) => {
    const d = dishes.get(i.dish);
    return d ? lacksAmount(d) : false;
  }).length : 0;
  const youth = drinks ? youthHint(draft, dishes) : null;
  const on = (fn: Parameters<typeof onPart>[2]) => edit((m) => onPart(m, part, fn));
  /** Back to a list: the sections' titles and menu prices go, so it is asked first when there are any. */
  const toList = () => {
    if (sub.sections.some((x) => x.title || x.kind === "set") && !confirm(drinks ? "Die Abschnitte der Getränke auflösen? Ihre Überschriften gehen verloren, die Getränke bleiben." : "Die Abschnitte auflösen? Ihre Überschriften und Menüpreise gehen verloren, die Speisen bleiben.")) return;
    on((x) => flatten(x, dishes));
  };
  /** New sections from the categories, also over sections there are (C2): those go, so it is asked first. */
  const byCategory = () => {
    if (sub.structure === "sections" && sub.sections.some((x) => x.title || x.kind === "set") && !confirm(drinks ? "Die Abschnitte der Getränke durch Abschnitte aus den Kategorien ersetzen? Die Getränke bleiben." : "Die Abschnitte durch Abschnitte aus den Kategorien ersetzen? Ihre Überschriften und Menüpreise gehen verloren, die Speisen bleiben.")) return;
    on((x) => sectionsFromCategories(x, dishes, order));
  };
  return (
    <div className="menu-bar" data-testid={drinks ? "drinks-bar" : "menu-bar"}>
      <span className="segmented small">
        <button className={sections ? "" : "active"} onClick={() => sections && toList()} data-testid={drinks ? "drinks-structure-list" : "structure-list"}>
          Liste
        </button>
        <button className={sections ? "active" : ""} onClick={() => !sections && on((x) => ({ structure: "sections", sections: x.sections.map((y) => ({ ...y, title: y.title || (drinks ? "Getränke" : "Speisen") })) }))} data-testid={drinks ? "drinks-structure-sections" : "structure-sections"}>
          Mit Abschnitten
        </button>
      </span>
      {count > 1 && (
        <button className="link" onClick={byCategory} data-testid={drinks ? "drinks-by-category" : "by-category"}>
          Abschnitte aus Kategorien
        </button>
      )}
      {count > 1 && (
        <span className="sort-by">
          <span className="muted">Sortieren:</span>
          {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
            <button key={k} className="link" onClick={() => on((x) => sortItems(x, k, dishes, undefined, order))}>
              {SORT_LABELS[k]}
            </button>
          ))}
        </span>
      )}
      {missing > 0 && (
        <span className="warn small" title="Unter „Kataloge“ nachtragen">
          ⚠ {drinks ? (missing === 1 ? "1 Getränk" : `${missing} Getränke`) : missing === 1 ? "1 Speise" : `${missing} Speisen`} ohne Allergenangaben
        </span>
      )}
      {guessed > 0 && (
        <span className="warn small" title="Unter „Kataloge“ ansehen: „Angaben ungeprüft“" data-testid={drinks ? "drinks-guessed" : "menu-guessed"}>
          ⚠ {guessed === 1 ? `1 ${drinks ? "Getränk" : "Speise"}` : `${guessed} ${drinks ? "Getränke" : "Speisen"}`} mit ungeprüften Angaben
        </span>
      )}
      {amounts > 0 && (
        <span className="warn small" title="Die Menge gehört bei Getränken zum Preis" data-testid="drinks-amounts">
          ⚠ {amounts === 1 ? "1 Getränk" : `${amounts} Getränke`} ohne Menge
        </span>
      )}
      {youth && (
        <p className="warn small youth" data-testid="youth-hint">
          ⚠ {youth}
        </p>
      )}
    </div>
  );
}

/** The sections of a part with their dishes, drinks and texts (S22: food and drinks each their own). */
function PartSections({ part, draft, data, edit, canEdit, target, setTarget, wide, pencils, sort, locked = false }: { part: Part; draft: MenuInput; data: Data; edit: (fn: (m: MenuInput) => MenuInput) => void; canEdit: boolean; target: string | undefined; setTarget: (id: string) => void; wide: boolean; pencils: boolean; sort: ReturnType<typeof useSortable>; locked?: boolean }) {
  const dishes = dishesById(data);
  const texts = textsById(data);
  const drinks = part === "drinks";
  const sub = partMenu(draft, part);
  const sections = sub.structure === "sections";
  const list = sub.sections;
  // Weinbegleitung (S22): a set menu's further prices, offered where drinks are in play or there are some.
  const pairing = drinksInPlay(data) || hasDrinks(draft);
  /**
   * The section the dishes picked go into, always one: tinted; a tap on another makes that one it.
   * With drinks (S22) a list can be it too, so a text can go to the food as well as to the drinks.
   */
  const selectable = sections || draft.sections.some((x) => partOf(x) === "drinks");
  const active = (id: string) => canEdit && selectable && id === target;
  /** A new section takes the dishes picked next. */
  const addSection = (x: MenuSection) => {
    edit((m) => onPart(m, part, (y) => ({ ...y, sections: [...y.sections, x] })));
    setTarget(x.id);
  };
  // The section whose „Zeiten im Netz“ are open (S62).
  const [timesFor, setTimesFor] = useState<string | null>(null);
  const timesSection = timesFor ? draft.sections.find((x) => x.id === timesFor) : undefined;
  const setSection = (id: string, change: Partial<MenuSection>) => edit((m) => ({ ...m, sections: m.sections.map((s) => (s.id === id ? { ...s, ...change, ...(change.kind === "set" ? { items: s.items.map((i) => ({ ...i, price: null })) } : change.kind === "group" ? { price: null, items: s.items.map((i) => catalogItem(i, dishes.get(i.dish))) } : {}) } : s)) }));
  const moveSection = (index: number, by: number) =>
    edit((m) =>
      onPart(m, part, (y) => {
        const next = [...y.sections];
        const [s] = next.splice(index, 1);
        next.splice(Math.max(0, Math.min(next.length, index + by)), 0, s!);
        return { ...y, sections: next };
      }),
    );
  return (
    <>
      {timesSection && <TimesSheet section={timesSection} onSave={(web) => [setSection(timesSection.id, { web }), setTimesFor(null)]} onClose={() => setTimesFor(null)} />}
      <div>
        {list.map((s, si) => (
          <section
            key={s.id}
            className={`card section-card ${s.kind === "set" ? "set" : ""} ${active(s.id) ? "active" : ""}`}
            onClick={() => selectable && setTarget(s.id)}
            aria-current={active(s.id) || undefined}
            data-testid={drinks ? "drinks-section" : "section"}
          >
            {sections && (
              <>
                <div className="section-head">
                  <input value={s.title} onChange={(e) => setSection(s.id, { title: e.target.value })} placeholder={s.kind === "set" ? "Name des Menüs" : drinks ? "Abschnitt, z. B. Biere" : "Abschnitt, z. B. Vorspeisen"} readOnly={!canEdit} aria-label="Titel des Abschnitts" />
                  {s.kind === "set" && <span className="set-badge">Menü</span>}
                  {canEdit && (
                    <span className="row" style={{ gap: 0 }}>
                      <button className="link" disabled={si === 0} onClick={() => moveSection(si, -1)} aria-label="Abschnitt nach oben">
                        ↑
                      </button>
                      <button className="link" disabled={si === list.length - 1} onClick={() => moveSection(si, 1)} aria-label="Abschnitt nach unten">
                        ↓
                      </button>
                      <button
                        className="link danger"
                        onClick={() => {
                          if (s.items.length && !confirm(`Abschnitt „${s.title || "ohne Titel"}“ mit ${s.items.length} ${drinks ? "Getränken" : "Speisen"} entfernen?`)) return;
                          edit((m) => onPart(m, part, (y) => ({ ...y, sections: y.sections.length > 1 ? y.sections.filter((x) => x.id !== s.id) : [emptySection(drinks ? "Getränke" : "")] })));
                        }}
                        aria-label="Abschnitt entfernen"
                      >
                        ✕
                      </button>
                    </span>
                  )}
                </div>
                <div className="section-extra">
                  <SymbolInput value={s.note} onValue={(v) => setSection(s.id, { note: v })} placeholder={drinks ? "Hinweis, z. B. alle Weine auch als Flasche" : "Hinweis, z. B. dazu Brot und Aioli"} readOnly={!canEdit} aria-label="Hinweis zum Abschnitt" />
                  {s.kind === "set" ? (
                    <PriceInput value={s.price} onChange={(price) => setSection(s.id, { price })} disabled={!canEdit} label="Preis des Menüs" />
                  ) : drinks ? (
                    <span />
                  ) : (
                    // Once over the column of the section's prices (S46): „30 cm“ over the pizzas.
                    <input className="price-head" value={s.priceLabel ?? ""} onChange={(e) => setSection(s.id, { priceLabel: e.target.value.slice(0, 40) || undefined })} placeholder="über den Preisen" title="Steht einmal über der Spalte der Preise, z. B. 30 cm über den Pizzen" readOnly={!canEdit} aria-label="Angabe über den Preisen" data-testid="section-price-head" />
                  )}
                </div>
                {s.kind === "set" && (pairing || s.priceLabel || s.variants?.length) && <SetPrices section={s} data={data} canEdit={canEdit} change={(c) => setSection(s.id, c)} />}
                {s.kind === "set" && s.items.length > 0 && (
                  <p className="hint course-count" data-testid="course-count">
                    {courseCount(s.items) === 1 ? "1 Gang" : `${courseCount(s.items)} Gänge`} · {((n) => (n === 1 ? "1 Speise" : `${n} Speisen`))(s.items.filter((i) => !isDrink(dishes.get(i.dish) ?? {})).length)}
                    {((n) => (n ? ` · ${n === 1 ? "1 Getränk" : `${n} Getränke`}` : ""))(s.items.filter((i) => isDrink(dishes.get(i.dish) ?? {})).length)}
                    {canEdit && s.items.length > 1 ? " · „+ oder“ zwischen zwei Speisen macht sie zu einem Gang zur Wahl" : ""}
                  </p>
                )}
                {/* Numbers only in a menu to choose from, by a switch of its own (12-wahlmenue.md § 11). */}
                {s.kind === "set" && s.items.some((x) => x.or) && (
                  <label className="check" style={{ fontSize: "0.88rem", margin: "0.4rem 0 0" }}>
                    <input type="checkbox" checked={s.numbers === true} disabled={!canEdit} onChange={(e) => setSection(s.id, { numbers: e.target.checked ? true : undefined })} data-testid="set-numbers" />
                    Nummern im Menü (die des Katalogs: „das Menü mit der 1, 5, 23 und 6“)
                  </label>
                )}
                {/* On a new page (S43): from the second section of the food on; the drinks have their own switch. */}
                {canEdit && !drinks && si > 0 && (
                  <label className="check" style={{ fontSize: "0.88rem", margin: "0.4rem 0 0" }}>
                    <input type="checkbox" checked={s.newPage === true} onChange={(e) => setSection(s.id, { newPage: e.target.checked ? true : undefined })} data-testid="section-new-page" />
                    Auf neuer Seite beginnen (Druck, PDF, Word)
                  </label>
                )}
                {/* Zeiten im Netz (S62, Stufe 1; Bild 1): only with the time control; a temporary section shows its time, a tap changes it. */}
                {timedOn(data) && (s.web || canEdit) && (
                  <div className="section-times">
                    {s.web ? (
                      <button className="time-mark" disabled={!canEdit} onClick={() => setTimesFor(s.id)} title="Zeiten im Netz ändern" data-testid="section-times">
                        🕒 {windowText(s.web)}
                        {s.web.print === "hide" ? " · nicht gedruckt" : s.web.print === "plain" ? " · gedruckt ohne Zeit" : ""}
                      </button>
                    ) : (
                      <button className="link" onClick={() => setTimesFor(s.id)} data-testid="section-times-add">
                        Zeiten im Netz …
                      </button>
                    )}
                  </div>
                )}
                {/* Subsections from the second level of the categories (S44): only when the section's dishes have two of them. */}
                {canEdit && s.kind !== "set" && secondLevelsIn(s, dishes).size >= 2 && (
                  <button className="link subs-from-categories" onClick={() => edit((m) => subsectionsFromCategories(m, s.id, dishes, drinks ? data.drinkCategories : data.categories))} data-testid="subs-from-categories">
                    Unterabschnitte aus Kategorien
                  </button>
                )}
                {canEdit && !drinks && (
                  <label className="check" style={{ fontSize: "0.88rem", margin: "0.4rem 0 0" }}>
                    <input type="checkbox" checked={s.kind === "set"} onChange={(e) => setSection(s.id, { kind: e.target.checked ? "set" : "group", priceLabel: undefined, ...(e.target.checked ? {} : { variants: undefined }) })} />
                    Menü mit Gesamtpreis (Gänge ohne Einzelpreis)
                  </label>
                )}
              </>
            )}
            <div data-sort-group={s.id} className={`sort-group ${sort.groupClass(s.id, s.items.length)}`}>
              {s.items.length === 0 && !(canEdit && sections) && <p className="empty">{drinks ? "Noch keine Getränke." : "Noch keine Speisen."}</p>}
              {s.items.map((it, ii) => {
                const d = dishes.get(it.dish);
                const t = d ? undefined : texts.get(it.dish);
                // A subsection (S44): a row of its own with its title, moved at its grip like a dish; the dishes below it belong to it.
                if (isSubsection(it)) {
                  const setSub = (sub: string) => edit((m) => ({ ...m, sections: m.sections.map((x) => (x.id === s.id ? { ...x, items: x.items.map((y, k) => (k === ii ? { ...y, sub } : y)) } : x)) }));
                  return (
                    <div key={it.dish} className={`item sub-item ${sort.rowClass({ group: s.id, index: ii })}`} data-sort-row data-testid="item-sub" onClick={(e) => e.stopPropagation()}>
                      <span className="no" aria-hidden="true" />
                      <input className="sub-title" value={it.sub ?? ""} onChange={(e) => setSub(e.target.value)} placeholder="Unterabschnitt, z. B. Vom Kalb" maxLength={60} readOnly={!canEdit} autoFocus={canEdit && !it.sub} aria-label="Titel des Unterabschnitts" data-testid="sub-title" />
                      {canEdit && (
                        <span className="moves">
                          <button onClick={() => edit((m) => removeDish(m, it.dish))} aria-label={`Unterabschnitt „${it.sub || "ohne Titel"}“ entfernen`} title="Unterabschnitt entfernen (die Speisen bleiben)" data-testid="sub-remove">
                            ✕
                          </button>
                          <span {...sort.handle({ group: s.id, index: ii }, it.sub || "Unterabschnitt")} data-testid="sub-grip">
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                              <path d={GRIP_PATH} stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                            </svg>
                          </span>
                        </span>
                      )}
                    </div>
                  );
                }
                if (t) {
                  return (
                    <div key={it.dish} className={`item text-item ${sort.rowClass({ group: s.id, index: ii })}`} data-sort-row data-testid="item-text">
                      <span className="no" aria-hidden="true">¶</span>
                      <span className="name">
                        {t.title ? <b>{t.title}</b> : null}
                        {t.body ? <span className="facts">{t.body}</span> : null}
                      </span>
                      <span />
                      {canEdit && (
                        <span className="moves">
                          <button onClick={() => edit((m) => removeDish(m, it.dish))} aria-label={`${textLabel(t)} von der Speisekarte nehmen`} title="Von der Speisekarte nehmen">
                            ✕
                          </button>
                          <span {...sort.handle({ group: s.id, index: ii }, textLabel(t))}>
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                              <path d={GRIP_PATH} stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                            </svg>
                          </span>
                        </span>
                      )}
                    </div>
                  );
                }
                if (!d) return null;
                const set = s.kind === "set";
                // The list at the entrance (S42): the catalogue's prices, not to change here.
                const differs = !set && !locked && it.price !== d.price;
                const setItem = (change: (y: typeof it) => typeof it) =>
                  edit((m) => ({ ...m, sections: m.sections.map((x) => (x.id === s.id ? { ...x, items: (set ? tidyCourses : (y: typeof x.items) => y)(x.items.map((y, k) => (k === ii ? change(y) : y))) } : x)) }));
                // The further prices (S19), each in a row of its own below, to change on this menu.
                const variants = set
                  ? []
                  : itemVariants(it, d).map((v, k) => (
                      <div key={v.label} className="variant-item" data-testid={`item-${d.number}-variant-${k}`}>
                        <span />
                        <span className="name">
                          {v.label}
                          {!locked && v.price !== d.variants[k]!.price && <span className="catalog">Katalog {d.variants[k]!.price === null ? "ohne Preis" : plainPrice(d.variants[k]!.price)}</span>}
                        </span>
                        <PriceInput
                          value={locked ? d.variants[k]!.price : v.price}
                          onChange={(price) => setItem((y) => ({ ...y, variants: [...itemVariants(y, d).filter((x) => !sameLabel(x.label, v.label)), { label: v.label, price }] }))}
                          disabled={!canEdit || locked}
                          label={`Preis ${d.name}, ${v.label}`}
                          testid={`price-${d.number}-${k}`}
                        />
                        <span />
                      </div>
                    ));
                // A set menu's courses (S32): a seam above each dish – „oder“, or the title of the course it begins.
                const alt = set && (Boolean(it.or) || Boolean(it.paired) || Boolean(s.items[ii + 1]?.or) || Boolean(s.items[ii + 1]?.paired));
                // A drink in a set menu (S52): to the course above it, or a course of its own with a title.
                const drinkInSet = set && isDrink(d);
                const seam = drinkInSet ? (
                  (canEdit || it.paired || it.course) && (
                    <div className={`seam ${it.paired ? "or" : ""}`} onClick={(e) => e.stopPropagation()}>
                      {it.paired ? (
                        <button className="or-chip on" disabled={!canEdit} onClick={() => setItem((y) => ({ ...y, paired: undefined }))} title="Antippen: eigener Gang" aria-pressed="true" data-testid={`paired-${d.number}`}>
                          zum Gang
                        </button>
                      ) : (
                        <>
                          <input className="course-title" value={it.course ?? ""} onChange={(e) => setItem((y) => ({ ...y, course: e.target.value || undefined }))} placeholder={canEdit ? "Gang benennen" : ""} maxLength={60} readOnly={!canEdit} aria-label={`Titel des Gangs mit ${d.name}`} data-testid={`course-${d.number}`} />
                          {ii > 0 && canEdit && (
                            <button className="or-chip" onClick={() => setItem((y) => ({ ...y, paired: true, course: undefined }))} title="Zum Gang darüber" aria-pressed="false" data-testid={`paired-${d.number}`}>
                              + zum Gang
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )
                ) : set && (canEdit || it.or || it.course) && (
                  <div className={`seam ${it.or ? "or" : ""}`} onClick={(e) => e.stopPropagation()}>
                    {it.or ? (
                      <button className="or-chip on" disabled={!canEdit} onClick={() => setItem((y) => ({ ...y, or: undefined }))} title="Antippen: eigener Gang" aria-pressed="true" data-testid={`or-${d.number}`}>
                        oder
                      </button>
                    ) : (
                      <>
                        <input className="course-title" value={it.course ?? ""} onChange={(e) => setItem((y) => ({ ...y, course: e.target.value || undefined }))} placeholder={canEdit ? "Gang benennen" : ""} maxLength={60} readOnly={!canEdit} aria-label={`Titel des Gangs mit ${d.name}`} data-testid={`course-${d.number}`} />
                        {ii > 0 && canEdit && (
                          <button className="or-chip" onClick={() => setItem((y) => ({ ...y, or: true, course: undefined }))} title="Zur Wahl mit der Speise darüber" aria-pressed="false" data-testid={`or-${d.number}`}>
                            + oder
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
                return (
                  <div key={it.dish} className={`item ${set ? "set" : ""} ${alt ? "in-course" : ""} ${sort.rowClass({ group: s.id, index: ii })}`} data-sort-row data-testid={`item-${d.number}`}>
                    {seam}
                    <span className="no">{d.number}</span>
                    <span className="name">
                      {d.name}
                      {d.allergens?.length ? <sup className="codes">{d.allergens.join(",")}</sup> : null}
                      {d.priceLabel && !set ? <span className="plabel"> · {d.priceLabel}</span> : null}
                      {differs ? <span className="catalog">Katalog {d.price === null ? "ohne Preis" : plainPrice(d.price)}</span> : <span className="facts">{d.category}</span>}
                    </span>
                    {drinkInSet ? (
                      <span className="facts drink-prices" data-testid={`set-drink-prices-${d.number}`}>
                        {s.variants?.length ? "in der Begleitung" : [d.priceLabel ? `${d.priceLabel} ${d.price === null ? "" : plainPrice(d.price)}` : d.price === null ? "" : plainPrice(d.price), ...d.variants.map((v) => `${v.label} ${v.price === null ? "" : plainPrice(v.price)}`)].filter(Boolean).join(" · ")}
                      </span>
                    ) : set ? (
                      <PriceInput value={it.surcharge ?? null} onChange={(c) => setItem((y) => ({ ...y, surcharge: c || undefined }))} disabled={!canEdit} label={`Aufpreis ${d.name}`} placeholder="+ €" testid={`surcharge-${d.number}`} />
                    ) : (
                      <PriceInput value={locked ? d.price : it.price} onChange={(price) => setItem((y) => ({ ...y, price }))} disabled={!canEdit || locked} label={d.priceLabel ? `Preis ${d.name}, ${d.priceLabel}` : `Preis ${d.name}`} testid={`price-${d.number}`} />
                    )}
                    {canEdit && (
                      <span className="moves">
                        {pencils && (
                          <a className="edit" href={`${isDrink(d) ? "#/getraenk/" : "#/speise/"}${d.id}`} onClick={rememberHere} aria-label={`${d.name} bearbeiten`} title="Im Katalog bearbeiten" data-testid={`menu-edit-${d.number}`}>
                            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" /></svg>
                          </a>
                        )}
                        <button onClick={() => edit((m) => removeDish(m, it.dish, scopeOf(m, s.id)))} aria-label={`${d.name} von der Speisekarte nehmen`} title="Von der Speisekarte nehmen" data-testid={`remove-${d.number}`}>
                          ✕
                        </button>
                        <span {...sort.handle({ group: s.id, index: ii }, d.name)} data-testid={`grip-${d.number}`}>
                          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                            <path d={GRIP_PATH} stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                          </svg>
                        </span>
                      </span>
                    )}
                    {!canEdit && variants.length > 0 && <span />}
                    {variants}
                  </div>
                );
              })}
            </div>
            {/* „+ Unterabschnitt“ (S44): once per subsection; the dishes tapped next come below it. */}
            {canEdit && sections && s.kind !== "set" && (
              <button
                className="link sub-add"
                onClick={(e) => {
                  e.stopPropagation();
                  setTarget(s.id);
                  edit((m) => ({ ...m, sections: m.sections.map((x) => (x.id === s.id ? { ...x, items: [...x.items, newSubsection()] } : x)) }));
                }}
                data-testid="sub-add"
              >
                + Unterabschnitt
              </button>
            )}
            {canEdit && sections && !active(s.id) && <p className="take-hint">{drinks ? "Antippen, um hier Getränke aufzunehmen." : "Antippen, um hier Speisen aufzunehmen."}</p>}
          </section>
        ))}
      </div>
      {canEdit && sections && (
        <div className="buttons">
          <button onClick={() => addSection(emptySection("", "group", part))} data-testid={drinks ? "drinks-add-section" : "add-section"}>
            + Abschnitt
          </button>
          {!drinks && <button onClick={() => addSection(emptySection("Menü", "set"))}>+ Menü mit Gesamtpreis</button>}
        </div>
      )}
    </>
  );
}

/**
 * A set menu's further prices (S22): what its price is for („4 Gänge“) and further prices below the
 * courses – „Korrespondierende Weinbegleitung 79,00 (l)“ – typed as in the workbook.
 */
function SetPrices({ section, data, canEdit, change }: { section: MenuSection; data: Data; canEdit: boolean; change: (c: Partial<MenuSection>) => void }) {
  const known = new Set(data.allergens.map((a) => a.code));
  const written = variantsText((section.variants ?? []).map((v) => ({ label: v.label, price: v.price, allergens: v.allergens })));
  const [text, setText] = useState(written);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(written), [written]);
  const commit = () => {
    const r = parseVariants(text, known);
    if ("error" in r) return setError(r.error);
    setError(null);
    const variants = r.value.length ? r.value.map((v) => ({ label: v.label, price: v.price, allergens: v.allergens })) : undefined;
    // Left without a change: nothing to save.
    if (JSON.stringify(variants ?? []) !== JSON.stringify(section.variants ?? [])) change({ variants });
  };
  return (
    <div className="set-prices" onClick={(e) => e.stopPropagation()}>
      <input value={section.priceLabel ?? ""} onChange={(e) => change({ priceLabel: e.target.value || undefined })} placeholder={`Preis gilt für, z. B. ${Math.max(2, courseCount(section.items))} Gänge`} aria-label="Wofür der Preis des Menüs gilt" readOnly={!canEdit} data-testid="set-price-label" />
      <input value={text} onChange={(e) => [setText(e.target.value), setError(null)]} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()} placeholder="Weitere Preise, z. B. Weinbegleitung 79,00 (l)" aria-label="Weitere Preise des Menüs" readOnly={!canEdit} data-testid="set-variants" />
      {error && <span className="error-text small">{error}</span>}
    </div>
  );
}

/**
 * Step 2, „Aussehen“: how the menu looks – the layout, its style (fonts, colours …; changed in
 * „Schriften, Farben, Abstände“), numbers and tags; beside it the PDF as it comes out.
 */
/**
 * Step 2, „Aussehen“: the layout, the numbers, and the style of this menu – chosen here, and changed
 * below for every menu that has it (DesignPanel); beside it the PDF as it comes out.
 */
function LookPanel({ draft, data, edit, canEdit, designs, setDesigns, mine, id, lang }: { draft: MenuInput; data: Data; edit: (fn: (m: MenuInput) => MenuInput) => void; canEdit: boolean; designs: Designs; setDesigns: (fn: (d: Designs) => Designs) => void; mine: string; id: string; lang: CardLang | null }) {
  const manage = canEdit && editsCatalog(store.role);
  const drinks = hasDrinks(draft);
  const named = designs.list.find((x) => x.id === mine) ?? designs.list[0]!;
  const house = named.id === designs.main;
  const usedBy = data.menus.filter((m) => m.style === named.id).length;
  const use = (id: string) => edit((m) => ({ ...m, style: id === designs.main ? "" : id }));
  function addStyle() {
    const name = prompt("Name des neuen Stils (z. B. Abendkarte). Er beginnt als Kopie des gewählten.", "");
    if (!name?.trim()) return;
    const fresh = { id: uuidv7(), name: name.trim().slice(0, 40), design: structuredClone(named.design) };
    setDesigns((d) => ({ ...d, list: [...d.list, fresh] }));
    edit((m) => ({ ...m, style: fresh.id }));
  }
  function rename() {
    const name = prompt("Neuer Name des Stils:", named.name);
    if (!name?.trim()) return;
    setDesigns((d) => ({ ...d, list: d.list.map((x) => (x.id === named.id ? { ...x, name: name.trim().slice(0, 40) } : x)) }));
  }
  function remove() {
    if (!confirm(`Stil „${named.name}“ löschen?${usedBy ? ` ${usedBy === 1 ? "Eine Speisekarte nutzt" : `${usedBy} Speisekarten nutzen`} ihn; sie bekommen den Hausstil.` : ""}`)) return;
    setDesigns((d) => ({ ...d, list: d.list.filter((x) => x.id !== named.id) }));
    edit((m) => ({ ...m, style: "" }));
  }
  return (
    <fieldset className="look" disabled={!canEdit}>
      <h3>{drinks ? "Darstellung der Speisen" : "Darstellung"}</h3>
      <div className="layouts">
        {LAYOUTS.map((l) => (
          <button key={l} className={draft.layout === l ? "active" : ""} onClick={() => edit((m) => ({ ...m, layout: l }))} data-testid={`layout-${l}`}>
            <b>{LAYOUT_LABELS[l]}</b> <span className="hint">{LAYOUT_HINTS[l]}</span>
          </button>
        ))}
      </div>
      {/* The drinks (S22): a layout of their own, often compact; on a new page when wanted. */}
      {drinks && draft.drinks && (
        <>
          <h3>Darstellung der Getränke</h3>
          <div className="layouts">
            {LAYOUTS.map((l) => (
              <button key={l} className={draft.drinks!.layout === l ? "active" : ""} onClick={() => edit((m) => ({ ...m, drinks: { ...(m.drinks as DrinksPart), layout: l } }))} data-testid={`drinks-layout-${l}`}>
                <b>{LAYOUT_LABELS[l]}</b> <span className="hint">{LAYOUT_HINTS[l]}</span>
              </button>
            ))}
          </div>
          {countsOf(draft, dishesById(data)).food > 0 && (
            <label className="check">
              <input type="checkbox" checked={draft.drinks.newPage} onChange={(e) => edit((m) => ({ ...m, drinks: { ...(m.drinks as DrinksPart), newPage: e.target.checked } }))} data-testid="drinks-new-page" />
              Getränke auf einer neuen Seite (Druck, PDF, Word)
            </label>
          )}
        </>
      )}

      <h3>Größe dieser Speisekarte</h3>
      <SizePanel draft={draft} data={data} edit={edit} design={named.design} id={id} lang={lang} />

      <h3>Stil dieser Speisekarte</h3>
      <div className="style-choice">
        {designs.list.map((x) => (
          <button key={x.id} className={mine === x.id ? "active" : ""} onClick={() => use(x.id)} data-testid={`menu-style-${x.name}`}>
            <span style={{ fontFamily: fontOf(x.design.styles.house.font).css, fontWeight: x.design.styles.house.bold ? 700 : 400, color: x.design.styles.house.color }}>{x.name}</span>
            <small>
              {fontOf(x.design.styles.dish.font).label} · {pageOf(x.design.page.format).label}
              {x.id === designs.main && x.name !== HOUSE_STYLE ? " · Hausstil" : ""}
            </small>
          </button>
        ))}
        {manage && designs.list.length < MAX_DESIGNS && (
          <button className="add" onClick={addStyle} data-testid="design-add">
            <span>+ Neuer Stil</span>
            <small>als Kopie des gewählten</small>
          </button>
        )}
      </div>
      <p className="hint style-actions">
        {house ? "Der Hausstil gilt für jede Speisekarte ohne eigenen Stil." : `${usedBy === 1 ? "Eine Speisekarte nutzt" : `${usedBy} Speisekarten nutzen`} „${named.name}“.`} Was du unten änderst, gilt für alle Speisekarten mit diesem Stil.
        {manage && (
          <>
            {" "}
            <button className="link inline" onClick={rename} data-testid="design-rename">
              Umbenennen
            </button>
            {!house && (
              <>
                {" · "}
                <button className="link inline" onClick={() => setDesigns((d) => ({ ...d, main: named.id }))} data-testid="design-main">
                  Zum Hausstil machen
                </button>
                {" · "}
                <button className="link inline danger" onClick={remove} data-testid="design-remove">
                  Löschen
                </button>
              </>
            )}
          </>
        )}
      </p>
    </fieldset>
  );
}

/**
 * „Größe dieser Speisekarte“ (T14): the dishes larger or smaller as a whole, for this menu only;
 * „An Seite anpassen“ finds the largest size that keeps its pages, then more space, then margins.
 */
function SizePanel({ draft, data, edit, design, id, lang }: { draft: MenuInput; data: Data; edit: (fn: (m: MenuInput) => MenuInput) => void; design: Design; id: string; lang: CardLang | null }) {
  const scale = draft.scale ?? NO_SCALE;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /** „An Seite anpassen“ found no room: said, so the tap does not seem lost. */
  const [full, setFull] = useState(false);
  const set = (next: MenuScale) =>
    edit((m) => {
      const { scale: _, ...rest } = m;
      const clean = scaleOf(next);
      return clean ? { ...rest, scale: clean } : rest;
    });
  async function fit() {
    setBusy(true);
    setError("");
    setFull(false);
    try {
      const [{ fitToPage }, { pageLayout }] = await Promise.all([import("../fit.ts"), import("../menuPdf.ts")]);
      const date = new Date();
      // The card as the PDF beside shows it: in its language (two take more room), as an insert when it is one (02.11.2026).
      const fitted = await fitToPage((s) => pageLayout(printMenuOf({ ...draft, scale: s }, data, date, design, id, lang)));
      set(fitted);
      setFull(isNoScale(fitted));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  const pct = (v: number) => `${Math.round(v * 100)} %`;
  const done = scaleSummary(scale);
  return (
    <div className="size-panel">
      <label className="size-row">
        <span>Größe</span>
        <input type="range" min={80} max={140} step={1} value={Math.round(scale.size * 100)} onChange={(e) => set({ ...scale, size: Number(e.target.value) / 100 })} data-testid="menu-size" />
        <output data-testid="menu-size-value">{pct(scale.size)}</output>
      </label>
      <div className="buttons">
        <button onClick={() => void fit()} disabled={busy} data-testid="menu-fit">
          {busy ? "Passe an …" : "An Seite anpassen"}
        </button>
        {!isNoScale(draft.scale) && (
          <button onClick={() => set(NO_SCALE)} data-testid="menu-size-reset">
            Zurück auf 100 %
          </button>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      {full && (
        <p className="hint" data-testid="menu-fit-full">
          Größer geht nicht, ohne dass eine Speise die Seite wechselt.
        </p>
      )}
      <p className="hint" data-testid="menu-size-hint">
        {isNoScale(draft.scale) ? "" : `${done}. `}Gilt nur für diese Speisekarte, in Druck, PDF und Word. Der Stil bleibt, die QR-Code-Speisekarte auch.
        {/* One size for every language of the card (B1, Martin 02.11.2026): said, since two languages need more room. */}
        {lang && ` „An Seite anpassen“ rechnet für ${lang.both ? `Deutsch und ${LANG_LABELS[lang.lang]}` : LANG_LABELS[lang.lang]}, wie das PDF daneben; die Größe gilt für die Speisekarte in jeder Sprache.`}
      </p>
    </div>
  );
}

/** A price as typed („5,90“); a wrong one shows red and keeps the last good one. */
/** „5,20 €“, „ohne Preis“: a price in a sentence. */
// „8,90 €“ never broken between the figure and its sign.
const euro = (cents: number | null) => (cents === null ? "ohne Preis" : `${plainPrice(cents)}\u00a0€`);

function PriceInput({ value, onChange, disabled, label, testid, placeholder = "Preis" }: { value: number | null; onChange: (cents: number | null) => void; disabled?: boolean; label: string; testid?: string; placeholder?: string }) {
  const [text, setText] = useState(plainPrice(value));
  const [bad, setBad] = useState(false);
  useEffect(() => setText(plainPrice(value)), [value]);
  const commit = () => {
    const r = parsePrice(text);
    if ("error" in r) return setBad(true);
    setBad(false);
    if (r.value !== value) onChange(r.value);
    setText(plainPrice(r.value));
  };
  return (
    <input
      className={`price ${bad ? "invalid" : ""}`}
      inputMode="decimal"
      value={text}
      onChange={(e) => [setText(e.target.value), setBad(false)]}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
      onClick={(e) => e.stopPropagation()}
      placeholder={placeholder}
      aria-label={label}
      readOnly={disabled}
      data-testid={testid}
    />
  );
}

/** After saving: prices of the menu that differ from the catalogue, each to take over or not. */
function PriceDialog({ diffs, onClose }: { diffs: ReturnType<typeof priceDiffs>; onClose: (message?: string) => void }) {
  const [chosen, setChosen] = useState(new Set(diffs.map((d) => d.dish)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function apply() {
    setBusy(true);
    try {
      const changes = diffs.filter((d) => chosen.has(d.dish)).map((d) => ({ id: d.dish, ...(d.to !== null ? { price: d.to } : {}), ...(d.variants.length ? { variants: d.variants.map((v) => ({ label: v.label, price: v.to })) } : {}) }));
      await store.change(() => api("/api/prices", { method: "PUT", body: { changes } }));
      onClose(`${chosen.size === 1 ? "1 Preis" : `${chosen.size} Preise`} in den Katalog übernommen`);
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  }
  return (
    <div className="overlay" role="dialog" aria-label="Preise übernehmen">
      <div className="dialog" data-testid="price-dialog">
        <h2>Gespeichert</h2>
        <p>{diffs.length === 1 ? "Ein Preis weicht" : `${diffs.length} Preise weichen`} vom Katalog ab. In den Katalog übernehmen? Neue Speisekarten starten dann mit dem neuen Preis.</p>
        {diffs.map((d) => (
          <label key={d.dish} className="diff">
            <input type="checkbox" checked={chosen.has(d.dish)} onChange={(e) => setChosen((c) => (e.target.checked ? new Set([...c, d.dish]) : new Set([...c].filter((x) => x !== d.dish))))} />
            <span>
              {d.number} {d.name}
            </span>
            <span className="prices">
              {d.to !== null && (
                <span>
                  {d.from === null ? "–" : plainPrice(d.from)} → <b>{plainPrice(d.to)}</b>
                </span>
              )}
              {d.variants.map((v) => (
                <span key={v.label}>
                  {v.label}: {v.from === null ? "–" : plainPrice(v.from)} → <b>{plainPrice(v.to)}</b>
                </span>
              ))}
            </span>
          </label>
        ))}
        {error && <p className="error-box">{error}</p>}
        <div className="buttons">
          <button className="primary" disabled={busy || chosen.size === 0} onClick={() => void apply()} data-testid="prices-apply">
            In den Katalog übernehmen
          </button>
          <button onClick={() => onClose("Gespeichert; die Preise gelten nur auf dieser Speisekarte")}>Nur auf dieser Speisekarte</button>
        </div>
      </div>
    </div>
  );
}

/** The PDF over the whole screen: what the printer gets (it prints the PDF). */
function Preview({ menu, design, lang, onClose }: { menu: MenuInput; design: Design; lang: CardLang | null; onClose: () => void }) {
  return (
    <div className="preview" role="dialog" aria-label="Druckansicht">
      <div className="preview-bar">
        <strong>Druckansicht ({pageOf(design.page.format).label})</strong>
        <button onClick={onClose} data-testid="preview-close">
          Schließen
        </button>
      </div>
      <div className="preview-pdf">
        <PdfPreview menu={menu} design={design} lang={lang} />
      </div>
    </div>
  );
}

/**
 * „In PDF-Link legen“ (S54; the names S59): this menu, as „PDF herunterladen“ makes it, into a PDF link
 * with a fixed address (made under „Im Netz“). The address stays; what stood there before is replaced.
 */
function PdfSlotOut({ data, pm, lang, id, disabled, outputs, save }: { data: Data; pm: () => PrintMenu; lang: CardLang | null; id: string | undefined; disabled: boolean; outputs: (fn: () => Promise<void>) => Promise<void>; save: () => Promise<string | null> }) {
  const slots = data.pdfSlots ?? [];
  const [chosen, setChosen] = useKept("out:pdf-slot", slots.find((s) => s.menuId === id)?.id ?? slots[0]?.id ?? "");
  const slot = slots.find((s) => s.id === chosen) ?? slots.find((s) => s.menuId === id) ?? slots[0]!;
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState("");
  // Später als PDF (S62 § 10, Bild 14): with the time control, into the link at a moment.
  const timed = timedOn(data);
  const [when, setWhen] = useState<When>({ kind: "now" });
  const send = () =>
    outputs(async () => {
      setSending(true);
      setDone("");
      try {
        const at = whenAt(when, "pdf");
        if (at !== null) {
          if (!Number.isFinite(at)) throw new Error("Bitte Tag und Uhrzeit angeben.");
          // Saved first: the PDF of the menu as it is kept, so its mark compares with it.
          const menuId = await save();
          if (!menuId) return;
          const m = pm();
          await planPdfSlot(m, data, slot.id, menuId, at, pdfMarkOf(m, data, lang));
          setWhen({ kind: "now" });
          setDone(`plan:${whenText(at, Date.now())}`);
          return;
        }
        const m = pm();
        const after = await publishPdfSlot(m, data, slot.id, id, pdfMarkOf(m, data, lang));
        setDone(after.url ?? "");
      } finally {
        setSending(false);
      }
    });
  return (
    <div className="card out-web" data-testid="out-pdf-slot">
      <h3>PDF-Link für deine Website</h3>
      <p className="hint">{slot.file && slot.menuTitle ? `In „${slot.name}“ liegt „${slot.menuTitle}“. Das neue PDF ersetzt es, der Link bleibt.` : `Das PDF dieser Speisekarte unter dem festen Link „${slot.name}“.`}</p>
      <div className="buttons">
        {slots.length > 1 && (
          <select className="pdf-slot-choice" value={slot.id} onChange={(e) => [setChosen(e.target.value), setDone("")]} aria-label="PDF-Link" data-testid="pdf-slot-choice">
            {slots.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <button className="primary" onClick={() => void send()} disabled={disabled || sending} data-testid="pdf-slot-publish">
          {sending ? "Legt hinein …" : when.kind !== "now" ? "Einplanen" : "In PDF-Link legen"}
        </button>
      </div>
      {timed && <WhenChoice what="pdf" value={when} onChange={setWhen} testid="pdf-slot-when" />}
      {timed && <PlannedList data={data} what="pdf" slot={slot.id} may />}
      {done.startsWith("plan:") ? (
        <p className="ok-text" data-testid="pdf-slot-planned">
          Geplant: {done.slice(5)} liegt dieses PDF unter „{slot.name}“.
        </p>
      ) : done && (
        <p className="ok-text" data-testid="pdf-slot-done">
          Liegt unter{" "}
          <a href={done} target="_blank" rel="noopener">
            {done}
          </a>
        </p>
      )}
    </div>
  );
}
